import { Fragment, useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Play, Square, Users, Clock, UserPlus, Pause, RotateCcw, Check,
  Copy, ExternalLink, Zap, Calendar, Award, UserCheck, UserX, AlertTriangle,
  RefreshCw, Swords, Trash2, ArrowUp, Eye, EyeOff, X, CreditCard,
  Mail, Send, AlertCircle, Plus, DollarSign, QrCode, Smartphone, Trophy, Crown, Share2, Download,
  Wallet, BarChart3, ChevronDown, Settings, ArrowLeftRight, Flag, ChevronUp, ArrowUpToLine, Pencil, UserRoundPlus,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { sportsApi, useSportsAuth } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import OpenPlayQRHub from '../components/OpenPlayQRHub';
import ConfirmModal from '../components/ConfirmModal';
import PaymentLinkModal from '../components/PaymentLinkModal';
import PlayerManagementPanel, { PaymentReconcileModal, balanceOf } from '../components/PlayerManagementPanel';
import SkillInput from '../components/SkillInput';
import { divisionFit, fittingDivision, hasRange } from '../utils/skillLevels';
import { fmt12 } from '../utils/sportsUtils';
import { fmtDate } from '../utils/dates';
import { usePublicBase } from '../utils/shortUrl';
import { useBackdropClose } from '../utils/backdropClose';
import { shareChampionsCard, downloadChampionsCard } from '../utils/championsCard';
import OpenPlayAwards, { HowRanked, isFairRule, isWinsRule, fairRecord, cardAwards } from '../components/OpenPlayAwards';
import { LateArrivalPrompt, LateArrivalModal, LateArrivalPill } from '../components/LateArrivalMatch';
import ReplacePlayerModal from '../components/ReplacePlayerModal';
import MakeMatchModal from '../components/MakeMatchModal';
import LastGameModal from '../components/LastGameModal';
import { EditNameModal, GiveSpotModal } from '../components/PlayerSpotModals';
import OrganizerAccessCard from '../components/OrganizerAccessCard';
import EventTabs from '../components/EventTabs';
import MatchupCard from '../components/MatchupCard';
import MedalBadge from '../components/MedalBadge';


const STATUS_COLORS = {
  registered: '#737373', active: '#22C55E', paused: '#F59E0B',
  playing: '#E63946', done: '#3B82F6', flagged: '#EF4444', waitlisted: '#8B5CF6',
  removing: '#737373', // transient: a Remove in progress (tap Remove again if it sticks)
};
const STATUS_ICONS = {
  registered: UserPlus, active: UserCheck, paused: Pause,
  playing: Zap, done: Check, flagged: AlertTriangle, waitlisted: Clock,
  removing: UserX,
};

const PAY_COLORS = {
  paid: '#22C55E', deposit_paid: '#3B82F6', partial: '#8B5CF6', pending: '#F59E0B',
  awaiting_payment: '#8B5CF6', at_venue: '#737373', free: '#404040', transferred: '#525252',
};
const PAY_LABELS = {
  paid: 'PAID', deposit_paid: '50% PAID', partial: 'PARTIAL', pending: 'PROOF ⏳',
  awaiting_payment: 'ONLINE ⏳', at_venue: 'AT VENUE', free: 'FREE', transferred: 'MOVED WITH SPOT',
};

function PayBadge({ status }) {
  if (!status) return null;
  const c = PAY_COLORS[status] || '#737373';
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${c}22`, color: c }}>
      {PAY_LABELS[status] || status}
    </span>
  );
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#737373';
  return (
    <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1"
      style={{ background: `${color}22`, color }}>
      {status}
    </span>
  );
}

// A refusal's text — detail may be a string or {code, message}. Handing the
// object straight to toast() crashed the whole page.
const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && typeof d === 'object' ? d.message : d) || fallback;
};

export default function SportsOpenPlayDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization } = useSportsAuth();
  const [session, setSession] = useState(null);
  const pubBase = usePublicBase(organization?.slug);
  const [players, setPlayers] = useState([]);
  const [courtQueues, setCourtQueues] = useState([]);
  const [poolSlots, setPoolSlots] = useState([]);
  const [divisionState, setDivisionState] = useState({});
  const [lateModal, setLateModal] = useState(null); // { divId } — late-arrival match preview
  // Minimized late-arrival prompts, per device: { divId: waiting count the coordinator last saw }.
  const lateMinKey = `opLateMin:${id}`;
  const [lateMin, setLateMinState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(lateMinKey) || '{}') || {}; } catch { return {}; }
  });
  const setLateMin = useCallback((next) => {
    setLateMinState(next);
    try { localStorage.setItem(lateMinKey, JSON.stringify(next)); } catch { /* private mode: per-visit only */ }
  }, [lateMinKey]);
  // Once a division's prompt no longer applies (match pushed, they got a game),
  // forget the minimize — the NEXT late arrivals get the full banner again.
  useEffect(() => {
    const stale = Object.keys(lateMin).filter(k => divisionState[k] && !divisionState[k].late_arrival?.show);
    if (stale.length) {
      const next = { ...lateMin };
      stale.forEach(k => { delete next[k]; });
      setLateMin(next);
    }
  }, [divisionState, lateMin, setLateMin]);
  const [tbaSlots, setTbaSlots] = useState([]);
  const [openGames, setOpenGames] = useState([]); // TBA games: saved seats + empty spots (2026-10-05)
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set after the session loads: live sessions land on Game Courts, everything
  // else lands on the Dashboard (the old 'courts' default pointed at a tab
  // that doesn't exist for non-active sessions).
  // ?tab=players deep-links straight to the money desk (used by pay-return).
  // The tab lives in the address (?tab=courts), so a refresh keeps the place.
  const [tab, setTab] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    if (['divisions', 'qr', 'invitations'].includes(q)) return 'setup';
    return ['courts', 'roster', 'players', 'leaderboard', 'dashboard', 'setup'].includes(q) ? q : '';
  });
  useEffect(() => {
    if (!tab) return;
    const u = new URL(window.location.href);
    if (u.searchParams.get('tab') !== tab) {
      u.searchParams.set('tab', tab);
      window.history.replaceState(window.history.state, '', u);
    }
  }, [tab]);
  const [reconcile, setReconcile] = useState(null); // { players, index, initial? } → PaymentReconcileModal
  const [actionLoading, setActionLoading] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [proofPlayer, setProofPlayer] = useState(null);
  const [paylinkPlayer, setPaylinkPlayer] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [scorerModal, setScorerModal] = useState(null); // { slot }
  const [addCourtModal, setAddCourtModal] = useState(null); // { divId, divName }
  const [podium, setPodium] = useState(null); // end-of-session podium data
  const [endModal, setEndModal] = useState(null); // { matches, busy } — End Session review
  const [resultModal, setResultModal] = useState(null); // { mode: 'declare'|'correct', slot, winner? }
  const [takeover, setTakeover] = useState(null); // { detail, retry(pin) } — a phone is scoring this game
  const [ownerBoard, setOwnerBoard] = useState(null); // per-division leaderboard, ranked like the podium
  const [replaceModal, setReplaceModal] = useState(null); // { slot, courtName } — Replace player (court ⋯)
  const [makeModal, setMakeModal] = useState(false); // Make a match (Up next)
  const [lastModal, setLastModal] = useState(false); // Last game (Up next)
  const [spotModal, setSpotModal] = useState(null); // { kind: 'name'|'give', player } — Players tab ⋯
  const [onCourtChoice, setOnCourtChoice] = useState(null); // { player, detail } — Done on a player on court
  const [courtPauseChoice, setCourtPauseChoice] = useState(null); // { court, live } — Pause this court (7.4)
  // In-flight guards: a double tap on Start / End / a set button used to send
  // two requests (the second came back as a red error toast after the success).
  const statusInFlight = useRef(false);
  const endInFlight = useRef(false);
  const setInFlight = useRef(false);
  const [changingStatus, setChangingStatus] = useState(null); // endpoint in flight
  const [hidingSession, setHidingSession] = useState(false);

  const removePlayer = (p) => {
    const paid = Number(p.amount_paid || 0);
    setConfirmModal({
      title: 'Remove Player',
      color: '#EF4444',
      confirmLabel: 'Remove Player',
      message: (
        <>
          Remove <b style={{ color: '#fff' }}>{p.name}</b>'s entry? A waitlisted player will be promoted if available.
          {p.status === 'playing' && (
            <span className="block mt-2 text-xs" style={{ color: '#737373' }}>
              They're on court now. Someone free takes their spot; if nobody is free, that match is cancelled.
            </span>
          )}
          {paid > 0 && (
            <span className="block mt-2 text-xs font-bold" style={{ color: '#F59E0B' }}>
              ⚠ {p.name} has ₱{paid.toLocaleString()} in recorded payments. Removing them does NOT
              refund or erase that money — it stays in the books. Settle any refund at the desk first.
            </span>
          )}
        </>
      ),
      onConfirm: async () => {
        setConfirmModal(null);
        setActionLoading(`remove-${p.id}`);
        try {
          const r = await sportsApi.delete(`/sports/open-play/${id}/players/${p.id}`);
          toast.success(r.data.promoted ? `Removed — ${r.data.promoted.name} promoted from waitlist!` : 'Player removed');
          await fetchAll();
        } catch (e) {
          toast.error(errMsg(e, 'Failed to remove'));
        } finally { setActionLoading(null); }
      },
    });
  };

  // Every collection goes through the review-then-confirm reconcile modal —
  // no one-tap "mark paid" anywhere on this page.
  const openCollect = (player, initial = null) => {
    const payable = players.filter(p => p.payment_status !== 'free');
    const idx = Math.max(0, payable.findIndex(p => p.id === player.id));
    setProofPlayer(null);
    setReconcile({ players: payable, index: idx, initial });
  };

  const fetchAll = useCallback(async () => {
    try {
      const requests = [
        sportsApi.get(`/sports/open-play/${id}`),
        sportsApi.get(`/sports/open-play/${id}/players`),
      ];
      const [sRes, pRes] = await Promise.all(requests);
      setSession(sRes.data);
      setPlayers(pRes.data);
      setTab(prev => (!prev || (prev === 'courts' && sRes.data.status !== 'active'))
        ? (sRes.data.status === 'active' ? 'courts' : 'dashboard') : prev);
      if (sRes.data.status !== 'active') {
        // An ended session keeps no live court data (dead A/B Wins buttons used to linger).
        setCourtQueues([]); setPoolSlots([]); setTbaSlots([]); setRecentResults([]);
      }
      sportsApi.get(`/sports/open-play/${id}/leaderboard`)
        .then(r => setOwnerBoard(r.data?.divisions || []))
        .catch(() => {});
      // Fetch court queues if session is active
      if (sRes.data.status === 'active') {
        try {
          const cRes = await sportsApi.get(`/sports/open-play/${id}/courts`);
          const cData = cRes.data;
          // Handle new format {courts, pool} or old format (array)
          if (Array.isArray(cData)) {
            setCourtQueues(cData);
            setPoolSlots([]);
            setTbaSlots([]);
            setOpenGames([]);
            setRecentResults([]);
          } else {
            setCourtQueues(cData.courts || []);
            setPoolSlots(cData.pool || []);
            setTbaSlots(cData.tba || []);
            setOpenGames(cData.open_games || []);
            setRecentResults(cData.recent_results || []);
            setDivisionState(cData.division_state || {});
          }
        } catch { /* ignore */ }
      }
      // Load invitations (always fetch — invitations are useful even in "open" mode)
      try {
        const iRes = await sportsApi.get(`/sports/open-play/${id}/invitations`);
        setInvitations(iRes.data || []);
      } catch { setInvitations([]); }
      // Revenue summary (only if fee > 0)
      if (parseFloat(sRes.data.entry_fee || 0) > 0) {
        try {
          const rRes = await sportsApi.get(`/sports/open-play/${id}/revenue`);
          setRevenue(rRes.data);
        } catch { /* ignore */ }
      }
    } catch {
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh when active
  useEffect(() => {
    if (session?.status !== 'active') return;
    const iv = setInterval(fetchAll, 10000);
    return () => clearInterval(iv);
  }, [session?.status, fetchAll]);

  const doAction = async (action, pid, body) => {
    setActionLoading(`${action}-${pid}`);
    try {
      const r = await sportsApi.put(`/sports/open-play/${id}/players/${pid}/${action}`, body || {});
      toast.success(r.data?.message
        || (r.data?.pause_requested ? 'They’ll be paused when their game ends'
          : r.data?.leaves_after_match ? 'They’ll leave when their game ends'
            : r.data?.pause_requested === false ? 'Pause request cancelled'
              : `Player ${action} successful`));
      await fetchAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === 'player_on_court' && action === 'done') {
        setOnCourtChoice({ player: players.find(x => x.id === pid) || { id: pid, name: 'This player' }, detail: d });
      } else {
        toast.error(errMsg(e, `Failed to ${action}`));
      }
    } finally {
      setActionLoading(null);
    }
  };

  // ── Coordinator tools (plan 7.3): level nudge, move division, fill a court ──
  const nudgeLevel = async (p, delta) => {
    setActionLoading(`level-${p.id}`);
    try {
      const r = await sportsApi.put(`/sports/open-play/${id}/players/${p.id}/level`, { delta });
      toast.success(`${p.name} is now level ${r.data.level}`);
      await fetchAll();
    } catch (e) {
      toast.error(errMsg(e, 'Could not change the level'));
    } finally { setActionLoading(null); }
  };

  const askMove = (p, toId) => {
    const target = divisionMap[toId];
    const from = divisionMap[p.division_id];
    if (!target || !from) return;
    const perMatch = session.game_mode === 'singles' ? 2 : 4;
    const leftBehind = players.filter(x => x.division_id === p.division_id && x.id !== p.id
      && ['active', 'playing'].includes(x.status)).length;
    const short = ['active', 'playing'].includes(p.status) && leftBehind > 0 && leftBehind < perMatch;
    setConfirmModal({
      title: 'Move Division',
      color: '#3B82F6',
      confirmLabel: `Move to ${target.name}`,
      message: (
        <>
          Move <b style={{ color: '#fff' }}>{p.name}</b> from {from.name} to <b style={{ color: '#fff' }}>{target.name}</b>?
          <span className="block mt-2 text-xs" style={{ color: '#737373' }}>
            Their seats in {from.name}&apos;s upcoming matches go to substitutes.
          </span>
          {short && (
            <span data-testid="op-move-short-warning" className="block mt-2 text-xs" style={{ color: '#F59E0B' }}>
              This leaves {from.name} with {leftBehind} — not enough for a game on its own. You can borrow a player with Fill this court, or move someone in.
            </span>
          )}
        </>
      ),
      onConfirm: async () => {
        setConfirmModal(null);
        setActionLoading(`move-${p.id}`);
        try {
          const r = await sportsApi.put(`/sports/open-play/${id}/players/${p.id}/division`, { division_id: toId });
          toast.success(r.data?.warning || `${p.name} moved to ${target.name}`);
          await fetchAll();
        } catch (e) {
          toast.error(errMsg(e, 'Could not move the player'));
        } finally { setActionLoading(null); }
      },
    });
  };

  const fillCourt = async (court) => {
    setActionLoading(`fill-${court.court_id}`);
    try {
      const r = await sportsApi.post(`/sports/open-play/${id}/courts/${court.court_id}/fill`);
      toast.success(r.data?.mode === 'queued' ? `${court.court_name}: the next match started`
        : `${court.court_name}: new match started from players free now`);
      await fetchAll();
    } catch (e) {
      toast.error(errMsg(e, 'Could not fill the court'));
    } finally { setActionLoading(null); }
  };

  // Pause / reopen a court (7.4): a paused court finishes its game, then takes
  // no new match until reopened; the live game can also go back to the front
  // of the queue at once.
  const pauseCourt = async (court, sendLive) => {
    const pin = await requirePin();
    if (pin === null) return;
    setCourtPauseChoice(null);
    setActionLoading(`pause-${court.court_id}`);
    try {
      const r = await sportsApi.put(`/sports/open-play/${id}/courts/${court.court_id}/pause`,
        { pin, send_live_to_queue: !!sendLive });
      const o = r.data?.live_match;
      toast.success(o === 'requeued' ? `${court.court_name} paused — the game went to the front of the queue`
        : o === 'finishing' ? `${court.court_name} will pause after this game`
        : `${court.court_name} paused`);
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(errMsg(e, 'Could not pause the court'));
    } finally { setActionLoading(null); }
  };

  const reopenCourt = async (court) => {
    const pin = await requirePin();
    if (pin === null) return;
    setActionLoading(`pause-${court.court_id}`);
    try {
      await sportsApi.put(`/sports/open-play/${id}/courts/${court.court_id}/reopen`, { pin });
      toast.success(`${court.court_name} reopened — it takes the next match`);
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(errMsg(e, 'Could not reopen the court'));
    } finally { setActionLoading(null); }
  };

  // ── Confirmation gates for the sensitive roster actions (Pause / Done).
  // Both pull a player out of play and are easy to mis-tap; require a
  // deliberate confirm. Additive actions (check-in, resume, reactivate) don't.
  const askPause = (p) => setConfirmModal({
    title: 'Pause Player',
    color: '#F59E0B',
    confirmLabel: 'Pause',
    message: (
      <>
        Pause <b style={{ color: '#fff' }}>{p.name}</b>?
        <span className="block mt-2 text-xs" style={{ color: '#737373' }}>
          They sit out until you Resume them. If they’re on court right now, they finish that game first and are paused when it ends. If their match is called while they’re paused, someone takes their spot.
        </span>
      </>
    ),
    onConfirm: () => { setConfirmModal(null); doAction('pause', p.id); },
  });

  const askDone = (p) => setConfirmModal({
    title: 'Mark as Done',
    color: '#EF4444',
    confirmLabel: 'Yes — mark Done',
    message: (
      <>
        Mark <b style={{ color: '#fff' }}>{p.name}</b> as <b style={{ color: '#EF4444' }}>DONE</b> for the day?
        <span className="block mt-2 text-xs" style={{ color: '#737373' }}>
          They leave the rotation and someone takes their spot in any match they’re lined up for. If they’re on court
          right now, you’ll choose what happens to that game first. If this is a mistake you can Reactivate them afterward.
        </span>
      </>
    ),
    onConfirm: () => { setConfirmModal(null); doAction('done', p.id); },
  });

  // PIN gating for owner actions (start/end/generate-slots/activate/complete).
  // Once verified, the PIN is cached in sessionStorage per session_id so
  // the owner doesn't have to re-enter it for every action on the same device.
  const pinKey = `op_pin_${id}`;
  const [pinPrompt, setPinPrompt] = useState(null); // { onSubmit, error }
  const cachedPin = () => sessionStorage.getItem(pinKey) || '';
  const clearPin = () => sessionStorage.removeItem(pinKey);

  // fresh: ask every time, even when this device already unlocked (Void — owner 2026-10-01).
  const requirePin = ({ fresh = false } = {}) => new Promise((resolve) => {
    if (!session?.has_scorer_pin) return resolve('');
    const existing = cachedPin();
    if (existing && !fresh) return resolve(existing);
    setPinPrompt({
      onSubmit: async (pin) => {
        try {
          await sportsApi.post(`/sports/open-play/${id}/verify-pin`, { pin });
          sessionStorage.setItem(pinKey, pin);
          setPinPrompt(null);
          resolve(pin);
        } catch (e) {
          setPinPrompt(p => p ? { ...p, error: e.response?.data?.detail?.message || 'Incorrect PIN' } : p);
        }
      },
      onCancel: () => { setPinPrompt(null); resolve(null); },
      error: '',
    });
  });

  const toggleHide = async () => {
    setHidingSession(true);
    try {
      const archived = !session.archived;
      await sportsApi.post(`/sports/open-play/${id}/archive`, { archived });
      toast.success(archived ? 'Session hidden from booking site' : 'Session visible on booking site');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update visibility');
    } finally { setHidingSession(false); }
  };

  // End Session never drops a game silently: the dialog lists every game still
  // being played (on a court or waiting for one) and the owner saves or
  // discards each result. The server refuses (409 live_matches) otherwise.
  const toLiveMatch = (s, courtName) => ({
    slot_id: s.id,
    court_name: courtName,
    team_a_name: (s.team_a_players || []).map(p => p.name).join(' & ') || 'Team A',
    team_b_name: (s.team_b_players || []).map(p => p.name).join(' & ') || 'Team B',
    score_a: s.score_a || 0,
    score_b: s.score_b || 0,
    pending_confirm: !!s.pending_confirm,
    pending_winner: s.pending_winner || null,
  });

  const mergeLive = (current, incoming) => {
    const seen = new Set((current || []).map(m => m.slot_id));
    return [...(current || []), ...(incoming || []).filter(m => !seen.has(m.slot_id))];
  };

  const openEndSession = async () => {
    if (endInFlight.current) return;
    endInFlight.current = true;
    try {
      const pin = await requirePin();
      if (pin === null) return;
      const { data } = await sportsApi.get(`/sports/open-play/${id}/courts`);
      const courtList = Array.isArray(data) ? data : (data?.courts || []);
      const live = [
        ...courtList.flatMap(c => (c.slots || [])
          .filter(s => s.status === 'active')
          .map(s => toLiveMatch(s, c.court_name))),
        ...(Array.isArray(data) ? [] : (data?.tba || [])).map(s => toLiveMatch(s, null)),
      ];
      setEndModal({ matches: mergeLive([], live), busy: false });
    } catch {
      toast.error('Could not load the courts — try again');
    } finally { endInFlight.current = false; }
  };

  const submitEndSession = async (finish, discard) => {
    if (endInFlight.current) return;
    endInFlight.current = true;
    setEndModal(m => (m ? { ...m, busy: true } : m));
    try {
      const pin = cachedPin();
      const r = await sportsApi.put(`/sports/open-play/${id}/end`, { ...(pin ? { pin } : {}), finish, discard });
      setEndModal(null);
      const saved = r.data?.saved || 0;
      toast.success(saved ? `Session ended — ${saved} result${saved === 1 ? '' : 's'} saved` : 'Session ended');
      await fetchAll();
      // Fetch podium data
      try {
        const pRes = await sportsApi.get(`/sports/open-play/${id}/podium`);
        if (pRes.data.first || (pRes.data.divisions || []).some(d => d.first)) setPodium(pRes.data);
      } catch { /* podium is optional */ }
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det?.code === 'live_matches') {
        // A game started while the dialog was open — add it for review.
        setEndModal(m => ({ matches: mergeLive(m?.matches, det.matches), busy: false }));
        toast.error(det.message);
        return;
      }
      if (e.response?.status === 403) clearPin();
      toast.error(det?.message || (typeof det === 'string' ? det : null) || 'Failed to end session');
      setEndModal(m => (m ? { ...m, busy: false } : m));
    } finally { endInFlight.current = false; }
  };

  const showPodium = async () => {
    try {
      const pRes = await sportsApi.get(`/sports/open-play/${id}/podium`);
      if (pRes.data.first || (pRes.data.divisions || []).some(d => d.first)) setPodium(pRes.data);
      else toast.info('No completed games — no podium for this session.');
    } catch (e) { toast.error(errMsg(e, 'Could not load the podium')); }
  };

  // Undo an accidental End within 2 hours (everyone goes back as they were).
  const reopenSession = () => setConfirmModal({
    title: 'Reopen session', color: '#22C55E', confirmLabel: 'Reopen',
    message: 'Put this session back live? Every player returns to how they were before End. Games cancelled at End stay cancelled, and the champions card comes off your gallery until you end again.',
    onConfirm: async () => {
      setConfirmModal(null);
      const pin = await requirePin();
      if (pin === null) return;
      try {
        await sportsApi.put(`/sports/open-play/${id}/reopen`, { pin });
        toast.success('Session reopened — it is live again');
        setTab('courts');
        await fetchAll();
      } catch (e) {
        if (e.response?.status === 403) clearPin();
        toast.error(errMsg(e, 'Could not reopen'));
      }
    },
  });

  const changeStatus = async (newStatus) => {
    const endpoint = {
      'registration_open': 'open-registration',
      'registration_closed': 'close-registration',
      'active': 'start',
      'ended': 'end',
    }[newStatus];
    if (!endpoint || statusInFlight.current) return;
    statusInFlight.current = true;
    try {
      // open/close registration are un-protected (no PIN); start/end are.
      let pin = '';
      if (newStatus === 'active' || newStatus === 'ended') {
        pin = await requirePin();
        if (pin === null) return;  // cancelled
      }
      setChangingStatus(newStatus);
      const r = await sportsApi.put(`/sports/open-play/${id}/${endpoint}`, pin ? { pin } : {});
      const msg = {
        active: `Session started — ${r.data?.slots_generated || 0} match${r.data?.slots_generated === 1 ? '' : 'es'} in Set 1`,
        ended: 'Session ended',
        registration_open: 'Registration is now OPEN', registration_closed: 'Registration is now CLOSED',
      }[newStatus];
      toast.success(msg);
      // A division short of players is skipped at Start (it used to burn Set 1
      // on zero matches) — say so, and what to do.
      (r.data?.warnings || []).forEach(w => toast.warning(w, { duration: 9000 }));
      if (newStatus === 'active') setTab('courts');  // straight to the live courts
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(errMsg(e, 'Action failed'));
      if (e.response?.status === 409) fetchAll();
    } finally {
      statusInFlight.current = false;
      setChangingStatus(null);
    }
  };

  // Confirm-gated registration toggles (parity with tournaments)
  const askStatus = (newStatus) => {
    const copy = {
      registration_open: {
        title: 'Open Registration', color: '#22C55E', confirmLabel: 'Open Registration',
        message: session?.status === 'registration_closed'
          ? 'Reopen public registration? Players will be able to sign up again through the public link.'
          : 'Open public registration? The session will appear on your booking page and anyone with the link can sign up.',
      },
      registration_closed: {
        title: 'Close Registration', color: '#F59E0B', confirmLabel: 'Close Registration',
        message: 'Close public registration? New players won\'t be able to self-register (you can still add walk-ins manually). You can reopen it anytime.',
      },
    }[newStatus];
    if (!copy) return changeStatus(newStatus);
    setConfirmModal({ ...copy, onConfirm: () => { setConfirmModal(null); changeStatus(newStatus); } });
  };

  const copyRegLink = () => {
    const slug = organization?.slug;
    if (!slug) return;
    const url = session?.short_code && pubBase
      ? `${pubBase}/p/${session.short_code}`
      : `${window.location.origin}/book/${slug}/open-play/${id}/register`;
    navigator.clipboard.writeText(url);
    toast.success('Registration link copied!');
  };

  const generateSlots = async () => {
    const pin = await requirePin();
    if (pin === null) return;
    setGenerating(true);
    try {
      const res = await sportsApi.post(`/sports/open-play/${id}/generate-slots`, pin ? { pin } : {});
      toast.success(`Generated ${res.data.slots_generated} matchup(s)`);
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to generate slots');
    } finally {
      setGenerating(false);
    }
  };

  const [generatingSet, setGeneratingSet] = useState(null); // null | 'open_play' | 'skill_based'
  const [skillSetModal, setSkillSetModal] = useState(false);
  const [divPickerType, setDivPickerType] = useState(null); // null | 'open_play' | 'skill_based'
  const [setDivision, setSetDivision] = useState(null); // division picked for skill set

  const startGenerateSet = (type) => {
    const divs = session?.divisions || [];
    if (divs.length > 1) { setDivPickerType(type); return; }
    const divId = divs[0]?.id || null;
    if (type === 'skill_based') { setSetDivision(divId); setSkillSetModal(true); }
    else generateSet('open_play', {}, divId);
  };

  const generateSet = async (type, skillOptions = {}, divisionId = null) => {
    if (setInFlight.current) return;
    setInFlight.current = true;
    try {
      const pin = await requirePin();
      if (pin === null) return;
      setGeneratingSet(type);
      const body = { pin: pin || undefined, type, division_id: divisionId || undefined, ...skillOptions };
      const res = await sportsApi.post(`/sports/open-play/${id}/generate-set`, body);
      const label = type === 'skill_based' ? 'Ranked Set' : 'Rotation Set';
      const divPart = res.data.division_name ? `${res.data.division_name} · ` : '';
      toast.success(`${divPart}${label} ${res.data.set_number} generated — ${res.data.slots_generated} matches scheduled`);
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to generate set');
    } finally {
      setInFlight.current = false;
      setGeneratingSet(null);
    }
  };

  // Declare a result: the owner types the final score (flip-card courts) or
  // records a walkover. The server refuses a winner the court score doesn't
  // support — it used to overwrite a real 11–10 with a fake 0–1.
  const askCompleteMatch = (slot, winner) => setResultModal({ mode: 'declare', slot, winner });

  const submitResult = async (mode, slot, body) => {
    // A walkover (automatic win) uses ONLY the PIN typed in its own box — never
    // the one remembered for this device (owner 2026-09-30).
    const pin = (body.walkover || body.takeover) ? (body.pin || '') : await requirePin();
    if (pin === null) return 'PIN required';
    setActionLoading(`${mode}-${slot.id}`);
    try {
      if (mode === 'declare') {
        await sportsApi.put(`/sports/open-play/${id}/slots/${slot.id}/complete`, { ...body, pin });
        toast.success(body.walkover ? 'Walkover recorded — queue advancing' : 'Result saved — queue advancing');
      } else {
        const r = await sportsApi.put(`/sports/open-play/${id}/slots/${slot.id}/correct-result`,
          { ...body, pin, from_winner: slot.winner });
        toast.success(r.data?.winner === slot.winner ? 'Score corrected' : 'Result corrected — winner changed');
      }
      setResultModal(null);
      await fetchAll();
      return null;
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === 'phone_scoring') {
        setTakeover({ detail: d, label: 'Take over & save',
          retry: (tpin) => submitResult(mode, slot, { ...body, takeover: true, pin: tpin }) });
        return null;
      }
      if (e.response?.status === 403 && !body.walkover && !body.takeover) clearPin();
      if (body.takeover) { toast.error(errMsg(e, 'Could not take over')); return errMsg(e, 'Could not take over'); }
      return errMsg(e, 'Could not save the result');
    } finally { setActionLoading(null); }
  };

  // Scorer reported a result that still waits for their Confirm — the owner
  // can confirm it from the court card (the court used to look still in play).
  const confirmPending = (slot) => submitResult('declare', slot, {
    winner: slot.pending_winner, score_a: slot.score_a || 0, score_b: slot.score_b || 0,
  }).then(err => { if (err) toast.error(err); });

  // Void / back to queue / skip — take a match out without a result.
  const voidSlot = async (slot, action, takeoverPin = null) => {
    const live = slot.status === 'active' && action !== 'requeue';
    const pin = takeoverPin !== null ? takeoverPin : await requirePin({ fresh: live });
    if (pin === null) return;
    setActionLoading(`void-${slot.id}`);
    try {
      const r = await sportsApi.put(`/sports/open-play/${id}/slots/${slot.id}/void`,
        { pin, action, ...(takeoverPin !== null ? { takeover: true } : {}) });
      const o = r.data?.outcome;
      toast.success(o === 'requeued' ? 'Back in line — players released'
        : o === 'skipped' ? 'Match removed from the queue' : 'Game voided — no result, players back in the queue');
      await fetchAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === 'phone_scoring') {
        setTakeover({ detail: d, label: 'Take over & void', retry: (tpin) => voidSlot(slot, action, tpin) });
        return;
      }
      if (e.response?.status === 403 && takeoverPin === null) clearPin();
      toast.error(errMsg(e, 'Could not remove that match'));
    } finally { setActionLoading(null); }
  };

  const askVoid = (slot, action) => {
    const names = [...(slot.team_a_players || []), ...(slot.team_b_players || [])].map(p => p.name).join(', ');
    const copy = action === 'requeue'
      ? { title: 'Back to queue', label: 'Back to queue', body: 'This match goes back in line and its players are free again. Nothing is recorded.' }
      : slot.status === 'queued' && slot.custom_match
        ? { title: 'Remove custom match', label: 'Remove', body: 'The match leaves the queue and nothing is recorded. Its players keep their other games.' }
      : slot.status === 'queued' && slot.late_insert
        ? { title: 'Remove late-arrival match', label: 'Remove', body: 'The match leaves the queue and nothing is recorded. The new players are waiting again, so the late-arrival prompt comes back.' }
      : slot.status === 'queued'
        ? { title: 'Skip match', label: 'Skip match', body: 'This match is removed from the queue. Nothing is recorded.' }
        : { title: 'Void game?', label: 'Void game', body: 'Are you sure you want to void this game? No result is saved — nobody’s wins, losses or rating change. The players go back to the queue and the court takes the next match. You’ll be asked for the session PIN.' };
    setConfirmModal({
      title: copy.title, color: '#F59E0B', confirmLabel: copy.label,
      message: (
        <>
          {copy.body}
          {names && <span className="block mt-2 text-xs" style={{ color: '#737373' }}>{names}</span>}
        </>
      ),
      onConfirm: () => { setConfirmModal(null); voidSlot(slot, action); },
    });
  };

  // ▲ ▼ ⤒ — move a queued match up, down or to the front (owner 2026-10-01).
  const moveSlot = async (slot, to) => {
    const pin = await requirePin();
    if (pin === null) return;
    setActionLoading(`move-${slot.id}`);
    try {
      const r = await sportsApi.put(`/sports/open-play/${id}/slots/${slot.id}/move`, { to, pin });
      toast.success(r.data?.started ? 'Moved — it started on a free court' : `Moved to #${r.data?.position}`);
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(errMsg(e, 'Could not move that match'));
      await fetchAll();
    } finally { setActionLoading(null); }
  };

  // Call a queued match to play NOW, jumping the algorithm order (owner override).
  // No court_id → server puts it on the first free court, or the TBA lane if full.
  const callSlot = async (slotId, courtId = null) => {
    const pin = await requirePin();
    if (pin === null) return;
    setActionLoading(`call-${slotId}`);
    try {
      const body = courtId ? { court_id: courtId, pin } : { pin };
      const res = await sportsApi.post(`/sports/open-play/${id}/slots/${slotId}/call`, body);
      toast.success(res.data.tba ? 'Called — waiting for a court (TBA)' : 'Called to court');
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to call match');
    } finally { setActionLoading(null); }
  };

  // Place a waiting match (TBA or pool) onto a specific free court.
  const assignCourt = async (slotId, courtId) => {
    const pin = await requirePin();
    if (pin === null) return;
    setActionLoading(`assign-${slotId}`);
    try {
      await sportsApi.put(`/sports/open-play/${id}/slots/${slotId}/assign-court`, { court_id: courtId, pin });
      toast.success('Placed on court');
      await fetchAll();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to assign court');
    } finally { setActionLoading(null); }
  };

  // Quick gender tag (owner) — feeds cold-start gender balancing. Cycles
  // none → M → F → none. Takes effect on the next generated set.
  const setPlayerGender = async (pid, gender) => {
    try {
      await sportsApi.put(`/sports/open-play/${id}/players/${pid}/gender`, { gender });
      setPlayers(prev => prev.map(p => (p.id === pid ? { ...p, gender: gender || null } : p)));
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to set gender');
    }
  };

  // Correct Result — fix the score (winner follows it) or flip a wrongly
  // declared winner. Server rejects once any involved player has a newer
  // completed match, and refuses a double tap.
  const askCorrectResult = (slot) => setResultModal({ mode: 'correct', slot });

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A', color: '#737373' }}>
      Loading...
    </div>
  );
  if (!session) return null;

  const divisionMap = {};
  (session.divisions || []).forEach(d => { divisionMap[d.id] = d; });

  // Group players by division
  const playersByDiv = {};
  players.forEach(p => {
    const did = p.division_id || 'unassigned';
    if (!playersByDiv[did]) playersByDiv[did] = [];
    playersByDiv[did].push(p);
  });

  return (
    <div className="min-h-screen op-console" style={{
      '--vglow': CONSOLE_GLOW, '--vacc': CONSOLE_ACCENT,
      background: `radial-gradient(1000px 440px at 50% -60px, ${CONSOLE_GLOW}, transparent 72%) no-repeat, #0A0A0A`,
    }}>
      <style>{CONSOLE_GLOW_CSS}</style>
      <SportsNavBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header — title + state, the stage's main actions, everything else
            under More, End Session set apart (owner 2026-09-27 console redesign) */}
        {(() => {
          const st = session.status;
          const live = st === 'active';
          const preLive = ['draft', 'registration_open', 'registration_closed'].includes(st);
          const startBtn = (
            <HeaderBtn variant="primary" icon={Play} testid="op-start"
              label={changingStatus === 'active' ? 'Starting…' : 'Start session'}
              disabled={changingStatus !== null} onClick={() => changeStatus('active')} />
          );
          const regOpen = session.public_registration === 'open';
          return (
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <button onClick={() => navigate('/sports/tournaments')} className="p-1 rounded-md"
                style={{ color: '#737373' }} data-testid="back-to-sessions" aria-label="Back to events">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight uppercase"
                    style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                    {session.name}
                  </h1>
                  <StatusSessionBadge status={st} />
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs flex-wrap" style={{ color: '#737373' }}>
                  {session.date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(session.date)}</span>}
                  {session.time_start && <span><Clock className="h-3 w-3 inline mr-0.5" />{fmt12(session.time_start)} – {fmt12(session.time_end)}</span>}
                </div>
              </div>
              <div className="flex-1" />
              <div className="flex flex-wrap items-center gap-2">
                {preLive && startBtn}
                {st === 'draft' && (
                  <HeaderBtn icon={Calendar} label="Edit event" testid="op-edit-event"
                    onClick={() => navigate(`/sports/open-play/${id}/edit`)} />
                )}
                {st === 'ended' && (
                  <HeaderBtn variant="primary" icon={Trophy} label="View podium" testid="op-view-podium" onClick={showPodium} />
                )}
                {st === 'ended' && session.ended_at && Date.now() - Date.parse(session.ended_at) < 2 * 3600 * 1000 && (
                  <HeaderBtn icon={RotateCcw} label="Reopen session" testid="op-reopen" onClick={reopenSession} />
                )}
                {st !== 'ended' && (
                  <HeaderBtn icon={UserPlus} label="Add player" testid="op-add-player" onClick={() => setAddModal(true)} />
                )}
                <MoreMenu testid="op-more" groups={[
                  { title: 'Sign-ups', items: [
                    st === 'draft' && { icon: Play, label: 'Open registration', testid: 'op-open-reg', onClick: () => askStatus('registration_open') },
                    st === 'registration_open' && { icon: Square, label: 'Close registration', testid: 'op-close-reg', onClick: () => askStatus('registration_closed') },
                    st === 'registration_closed' && { icon: Play, label: 'Reopen registration', testid: 'op-reopen-reg', onClick: () => askStatus('registration_open') },
                    live && regOpen && { icon: Square, label: 'Close registration', testid: 'op-live-close-reg', disabled: changingStatus !== null, onClick: () => askStatus('registration_closed') },
                    live && !regOpen && { icon: Play, label: 'Open registration', testid: 'op-live-open-reg', disabled: changingStatus !== null, onClick: () => askStatus('registration_open') },
                    (st === 'registration_open' || live) && { icon: Copy, label: 'Copy registration link', testid: 'op-copy-link', onClick: copyRegLink },
                  ] },
                  { title: 'Match queue', items: [
                    live && { icon: RefreshCw, label: generatingSet === 'open_play' ? 'Generating…' : 'Generate rotation set',
                      hint: 'Everyone plays — fresh partners', testid: 'op-generate-open-set', disabled: generatingSet !== null,
                      onClick: () => startGenerateSet('open_play') },
                    live && { icon: Swords, label: generatingSet === 'skill_based' ? 'Generating…' : 'Generate ranked set',
                      hint: 'Best vs best — the showcase / finals round', testid: 'op-generate-skill-set', disabled: generatingSet !== null,
                      onClick: () => startGenerateSet('skill_based') },
                  ] },
                  { title: 'Event', items: [
                    ['registration_open', 'registration_closed'].includes(st) && { icon: Calendar, label: 'Edit event', testid: 'op-edit-event', onClick: () => navigate(`/sports/open-play/${id}/edit`) },
                    organization?.slug && pubBase && { icon: ExternalLink, label: 'View public page', testid: 'op-public-page',
                      onClick: () => window.open(`${window.location.origin}/book/${organization.slug}/open-play/${id}`, '_blank') },
                    { icon: session.archived ? Eye : EyeOff, label: hidingSession ? '…' : (session.archived ? 'Show on site' : 'Hide from site'),
                      testid: 'op-toggle-hidden', onClick: toggleHide },
                  ] },
                ]} />
                {live && (
                  <HeaderBtn variant="danger" icon={Square} label="End session" testid="op-end" onClick={openEndSession} />
                )}
              </div>
            </div>
          );
        })()}
        {confirmModal && <ConfirmModal {...confirmModal} onCancel={() => setConfirmModal(null)} />}
        {takeover && (
          <TakeoverModal detail={takeover.detail} label={takeover.label} needsPin={!!session?.has_scorer_pin}
            onCancel={() => setTakeover(null)}
            onConfirm={async (tpin) => { const t = takeover; setTakeover(null); setResultModal(null); await t.retry(tpin); }} />
        )}
        {lateModal && (
          <LateArrivalModal sessionId={id} divisionId={lateModal.divId} requirePin={requirePin} clearPin={clearPin}
            onClose={() => setLateModal(null)}
            onPushed={async () => { setLateModal(null); await fetchAll(); }} />
        )}
        {spotModal?.kind === 'name' && (
          <EditNameModal sessionId={id} player={spotModal.player} requirePin={requirePin} clearPin={clearPin}
            onClose={() => setSpotModal(null)}
            onDone={async () => { setSpotModal(null); await fetchAll(); }} />
        )}
        {spotModal?.kind === 'give' && (
          <GiveSpotModal sessionId={id} player={spotModal.player} requirePin={requirePin} clearPin={clearPin}
            queuedSeats={(() => {
              const pid = spotModal.player.id;
              const has = sl => [...(sl.team_a || []), ...(sl.team_b || [])].includes(pid);
              const mine = poolSlots.filter(sl => sl.division_id === spotModal.player.division_id);
              return [...tbaSlots.filter(has).map(() => 'the called match'),
                      ...mine.map((sl, i) => (has(sl) ? `#${i + 1}` : null)).filter(Boolean)];
            })()}
            onCourtNow={courtQueues.some(c => (c.slots || []).some(sl => sl.status === 'active' && sl.court_id
              && [...(sl.team_a || []), ...(sl.team_b || []), ...Object.values(sl.stand_ins || {})].includes(spotModal.player.id)))}
            onClose={() => setSpotModal(null)}
            onDone={async () => { setSpotModal(null); await fetchAll(); }} />
        )}
        {lastModal && (
          <LastGameModal sessionId={id} divisions={session.divisions || []}
            requirePin={requirePin} clearPin={clearPin}
            onClose={() => setLastModal(false)}
            onDone={async () => { setLastModal(false); await fetchAll(); }} />
        )}
        {makeModal && (
          <MakeMatchModal sessionId={id} divisions={session.divisions || []}
            requirePin={requirePin} clearPin={clearPin}
            onClose={() => setMakeModal(false)}
            onDone={async () => { setMakeModal(false); await fetchAll(); }} />
        )}
        {replaceModal && (
          <ReplacePlayerModal sessionId={id} slot={replaceModal.slot} courtName={replaceModal.courtName}
            requirePin={requirePin} clearPin={clearPin}
            onClose={() => setReplaceModal(null)}
            onDone={async () => { setReplaceModal(null); await fetchAll(); }} />
        )}

        {/* Status strip — one line instead of four big cards and a red banner.
            Money is routine before/during a night: amber link, not an alarm. */}
        {(() => {
          const live = session.status === 'active';
          const count = (stt) => players.filter(p => p.status === stt).length;
          const paused = count('paused');
          const owing = players.filter(p => p.payment_status !== 'free' && balanceOf(p) > 0);
          const owingTotal = owing.reduce((sum, p) => sum + balanceOf(p), 0);
          return (
            <div data-testid="op-status-strip" className="op-card flex flex-wrap items-center gap-2 mb-5 p-2 rounded-xl"
              style={{ background: '#141414', border: '1px solid #262626' }}>
              <StripChip value={players.length} label={players.length === 1 ? 'player' : 'players'} testid="op-strip-total" />
              {live && <StripChip value={count('playing')} label="on court" color="#FF6B76" testid="op-strip-playing" />}
              <StripChip value={count('active')} label={live ? 'waiting' : 'checked in'} color="#4ADE80" testid="op-strip-active" />
              {paused > 0 && <StripChip value={paused} label="paused" color={C.amber} testid="op-strip-paused" />}
              {live && (session.divisions || []).map(d => {
                const st = divisionState[d.id]?.late_arrival;
                if (!st?.show || (lateMin[d.id] == null && tab === 'courts')) return null;
                const n = (st.waiting || []).length;
                return (
                  <LateArrivalPill key={`late-pill-${d.id}`} state={st}
                    divisionName={(session.divisions || []).length > 1 ? d.name : null}
                    pulse={lateMin[d.id] != null && n > lateMin[d.id]}
                    onPulseEnd={() => setLateMin({ ...lateMin, [d.id]: n })}
                    onOpen={() => setLateModal({ divId: d.id })} />
                );
              })}
              {owingTotal > 0 && tab !== 'players' && (
                <button data-testid="op-outstanding-pill" onClick={() => setTab('players')}
                  className="op-amber-soft sm:ml-auto inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#0B0B0B', color: '#FCD34D', border: `1px solid ${C.amber}44` }}
                  title={`${owing.length} player${owing.length === 1 ? '' : 's'} still owe`}>
                  <Wallet className="h-3.5 w-3.5 self-center" />
                  <b style={{ fontFamily: 'Oswald, sans-serif', fontSize: 17, color: C.amber }}>₱{owingTotal.toLocaleString()}</b>
                  to collect · Payments →
                </button>
              )}
            </div>
          );
        })()}

        {/* Tabs — ordered by the night's jobs; setup pages grouped under Setup */}
        {(() => {
          const live = session.status === 'active';
          const defs = {
            courts: { key: 'courts', label: 'Courts', icon: Zap,
              caption: 'Live courts and the match queue. Wrong result? Fix it under Recent Results at the bottom.' },
            roster: { key: 'roster', label: 'Players', icon: UserCheck,
              caption: 'Check players in when they arrive; pause, resume or finish them.' },
            players: { key: 'players', label: 'Payments', icon: Wallet,
              caption: 'The money desk: collect entry fees, review GCash proofs, see who still owes.' },
            leaderboard: { key: 'leaderboard', label: 'Leaderboard', icon: Trophy,
              caption: 'Standings per division — ranked like the podium.' },
            dashboard: { key: 'dashboard', label: 'Overview', icon: BarChart3,
              caption: 'Money, sign-ups and match progress at a glance. View-only.' },
            setup: { key: 'setup', label: 'Setup', icon: Settings,
              caption: 'Divisions, QR codes and invitations.',
              badge: invitations.filter(i => i.status === 'pending').length },
          };
          const order = live
            ? ['courts', 'roster', 'players', 'leaderboard', 'dashboard', 'setup']
            : ['dashboard', 'roster', 'players', 'leaderboard', 'setup'];
          return <EventTabs testPrefix="op-tab" active={tab} onChange={setTab} tabs={order.map(k => defs[k])} />;
        })()}

        {/* Dashboard tab (view-only overview — collecting happens on Payments) */}
        {tab === 'dashboard' && (
          <>
            <OrganizerAccessCard apiBase={`/sports/open-play/${id}`} />
            {revenue && parseFloat(session.entry_fee || 0) > 0 && (
              <RevenueSummary revenue={revenue} />
            )}
            <OpenPlayDashboard sessionId={id} session={session}
              onCollect={(pid) => {
                const pl = players.find(p => p.id === pid);
                if (pl) openCollect(pl);
              }} />
          </>
        )}

        {/* Payments tab — the money desk (reconcile modal, proofs, settlement) */}
        {tab === 'players' && (
          <PlayerManagementPanel
            mode="open_play"
            eventId={id}
            players={players}
            entryFee={parseFloat(session.entry_fee || 0)}
            divisions={session.divisions || []}
            onSendLink={(p) => setPaylinkPlayer(p)}
            onRefresh={fetchAll}
          />
        )}

        {/* Court Queue tab */}
        {tab === 'courts' && (
          <div className="space-y-4">
            {/* Recent Results now render at the BOTTOM of this tab (owner preference). */}
            {/* Courts heading — the court actions live with the courts */}
            {session.status === 'active' && (
              <div className="flex flex-wrap items-center gap-2" data-testid="op-courts-head">
                <h3 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>Courts</h3>
                <span className="text-xs" style={{ color: '#737373' }}>
                  {courtQueues.filter(c => !c.paused).length} of {courtQueues.length} open · 12–16 players per court recommended
                </span>
                <div className="flex-1" />
                {(session.divisions || []).map(div => (
                  <HeaderBtn key={div.id} icon={Plus} testid={`add-court-${div.id}`}
                    label={`Open court${(session.divisions || []).length > 1 ? ` (${div.name})` : ''}`}
                    onClick={() => setAddCourtModal({ divId: div.id, divName: div.name })} />
                ))}
              </div>
            )}
            {session.status === 'active' && (session.divisions || []).map(d => lateMin[d.id] == null && (
              <LateArrivalPrompt key={`late-${d.id}`} state={divisionState[d.id]?.late_arrival}
                divisionName={(session.divisions || []).length > 1 ? d.name : null}
                onCreate={() => setLateModal({ divId: d.id })}
                onMinimize={() => setLateMin({ ...lateMin,
                  [d.id]: (divisionState[d.id]?.late_arrival?.waiting || []).length })} />
            ))}
            {courtQueues.length === 0 ? (
              <div className="text-center py-12 rounded-xl" style={{ background: '#141414', border: '1px solid #262626' }}>
                <Swords className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
                <p style={{ color: '#737373' }}>No courts in this session yet — tap Open court.</p>
              </div>
            ) : (
              <>
              {/* ── Court Grid: Active Zone only (first 4 slots per court) ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {courtQueues.map(court => {
                  const activeSlots = court.slots.filter((_, si) => si < 4);
                  return (
                    <div key={court.court_id} data-testid={`court-card-${court.court_id}`}
                      className={`rounded-xl overflow-hidden ${activeSlots.some(sl => sl.status === 'active') ? 'op-live' : court.paused ? 'op-dim' : 'op-card'}`}
                      style={{ background: '#141414', border: '1px solid #262626' }}>
                      <div className="px-4 py-3 flex items-center justify-between"
                        style={{ background: '#1C1C1C', borderBottom: '1px solid #262626' }}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold uppercase"
                            style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                            {court.court_name}
                          </span>
                          {activeSlots.some(sl => sl.status === 'active') && <span className="op-live-dot" aria-label="Game in progress" />}
                          <span className="text-xs" style={{ color: '#737373' }}>{court.division_name}</span>
                          {court.paused && (
                            <span data-testid={`court-paused-badge-${court.court_id}`}
                              className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${court.pause_after_game ? 'op-attn' : ''}`}
                              style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B40' }}>
                              {court.pause_after_game ? 'Pauses after this game' : 'Paused'}
                            </span>
                          )}
                        </div>
                        {session.status === 'active' && (
                          court.paused ? (
                            <button data-testid={`court-reopen-${court.court_id}`}
                              disabled={actionLoading === `pause-${court.court_id}`}
                              onClick={() => reopenCourt(court)}
                              className="text-xs px-2.5 py-1.5 rounded-lg font-bold inline-flex items-center gap-1"
                              style={{ background: '#161616', color: '#E5E5E5', border: '1px solid #2A2A2A' }}>
                              <Play className="h-3 w-3" /> Reopen
                            </button>
                          ) : (
                            <button data-testid={`court-pause-${court.court_id}`}
                              disabled={actionLoading === `pause-${court.court_id}`}
                              onClick={() => setCourtPauseChoice({ court, live: activeSlots.find(sl => sl.status === 'active') || null })}
                              className="text-xs px-2.5 py-1.5 rounded-lg font-bold inline-flex items-center gap-1"
                              style={{ background: 'transparent', color: '#A3A3A3', border: '1px solid #2A2A2A' }}
                              title="Stop this court taking new matches (e.g. a renter needs it)">
                              <Pause className="h-3 w-3" /> Pause this court
                            </button>
                          )
                        )}
                      </div>
                      <div className="p-3 space-y-2">
                        {activeSlots.length === 0 && court.paused && (
                          <div className="text-center py-3" data-testid={`court-paused-${court.court_id}`}>
                            <p className="text-xs" style={{ color: '#F59E0B' }}>
                              Court paused — it takes no new matches. The queue runs on the other courts.
                            </p>
                          </div>
                        )}
                        {activeSlots.length === 0 && !court.paused && (
                          <div className="text-center py-3" data-testid={`court-idle-${court.court_id}`}>
                            <p className="text-xs" style={{ color: '#555' }}>
                              Court idle — it claims the next match from the shared queue when one can start
                            </p>
                            {session.status === 'active' && (
                              <button data-testid={`fill-court-${court.court_id}`}
                                disabled={actionLoading === `fill-${court.court_id}`}
                                onClick={() => fillCourt(court)}
                                className="mt-2 px-3 py-1.5 rounded-lg text-xs font-bold"
                                style={{ background: '#161616', color: '#E5E5E5', border: '1px solid #2A2A2A' }}
                                title="Start a match here now: the next queued match if it can start, otherwise players free right now">
                                {actionLoading === `fill-${court.court_id}` ? 'Filling…' : 'Fill this court'}
                              </button>
                            )}
                          </div>
                        )}
                        {activeSlots.map((slot, si) => {
                          const isActive = slot.status === 'active';
                          const taPlayers = slot.team_a_players || [];
                          const tbPlayers = slot.team_b_players || [];
                          const canScore = (isActive || (slot.status === 'queued' && si === 0)) && slot.qr_token;
                          const scorerUrl = slot.qr_token ? `${window.location.origin}/sports/score/${slot.qr_token}` : null;
                          const started = !!slot.pending_confirm || (slot.score_a || 0) + (slot.score_b || 0) > 0;
                          const since = slot.on_court_at || slot.started_at;
                          const mins = since ? Math.max(0, Math.round((Date.now() - Date.parse(since)) / 60000)) : null;
                          const chip = { background: '#0B0B0B', color: '#A3A3A3', border: '1px solid #262626' };
                          if (!isActive) {
                            return (
                              <div key={slot.id} data-testid={`slot-${slot.id}`} className="p-3 rounded-lg"
                                style={{ background: '#0A0A0A', border: '1px solid #1C1C1C' }}>
                                <div className="flex items-center justify-between mb-2 gap-2">
                                  <span className="text-[10px] font-bold uppercase" style={{ color: '#737373', letterSpacing: 1 }}>
                                    Next on this court{slot.set_number ? ` · Set ${slot.set_number}` : ''}
                                  </span>
                                  {canScore && scorerUrl && (
                                    <button data-testid={`slot-scorer-${slot.id}`} onClick={() => setScorerModal({ slot, scorerUrl })}
                                      className="text-xs px-2.5 py-1.5 rounded-lg font-bold inline-flex items-center gap-1"
                                      style={{ background: '#161616', color: '#E5E5E5', border: '1px solid #2A2A2A' }}>
                                      <QrCode className="h-3 w-3" /> Score
                                    </button>
                                  )}
                                </div>
                                <TeamMatchup taPlayers={taPlayers} tbPlayers={tbPlayers} />
                              </div>
                            );
                          }
                          const names = (ps, align) => (
                            <div className={`min-w-0 ${align === 'right' ? 'text-right' : ''}`}>
                              {ps.map(p => (
                                <Fragment key={p.id}>
                                  <p className="text-sm sm:text-base font-bold truncate"
                                    style={{ color: align === 'right' ? '#6EA8FF' : '#FF6B76', lineHeight: 1.45 }}>{p.name}</p>
                                  {p.playing_for && (
                                    <p data-testid={`stand-in-${p.id}`} className="text-[10.5px] font-bold -mt-0.5 mb-0.5" style={{ color: '#A3A3A3' }}>
                                      ↻ playing for {p.playing_for.name}
                                    </p>
                                  )}
                                </Fragment>
                              ))}
                            </div>
                          );
                          return (
                            <div key={slot.id} data-testid={`slot-${slot.id}`}>
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                {slot.set_number && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase" style={chip}>
                                    {slot.set_type === 'skill_based' ? 'Ranked ' : ''}Set {slot.set_number}
                                  </span>
                                )}
                                {mins != null && <span className="text-[11px]" style={{ color: '#737373' }}>{mins} min</span>}
                                {(slot.fillers || []).length > 0 && (
                                  <span data-testid={`slot-filler-${slot.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                                    style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B30' }}
                                    title="Borrowed from another division so this short division can play">
                                    {(slot.fillers || []).map(fid => [...taPlayers, ...tbPlayers]
                                      .find(x => x.id === fid)?.name || 'Player').join(', ')} filling in
                                  </span>
                                )}
                              </div>
                              {/* scoreboard */}
                              <div className="grid items-center gap-2 py-2" style={{ gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)' }}>
                                {names(taPlayers, 'left')}
                                <span data-testid={`slot-live-score-${slot.id}`} key={`${slot.score_a || 0}-${slot.score_b || 0}`}
                                  className="op-score op-score-flash font-bold tabular-nums px-3 sm:px-4 rounded-xl text-3xl sm:text-4xl"
                                  style={{ fontFamily: 'Oswald, sans-serif', background: '#050505', border: '1px solid #262626', color: '#fff', letterSpacing: 1 }}>
                                  {slot.score_a || 0}<span style={{ color: '#444', margin: '0 6px' }}>–</span>{slot.score_b || 0}
                                </span>
                                {names(tbPlayers, 'right')}
                              </div>
                              {(() => {
                                const stand = [...taPlayers, ...tbPlayers].filter(p => p.playing_for);
                                if (!stand.length) return null;
                                return (
                                  <p data-testid={`stand-in-note-${slot.id}`} className="text-[11.5px] mb-2 px-2.5 py-1.5 rounded-lg leading-snug"
                                    style={{ color: '#FCD34D', background: '#F59E0B10', border: '1px solid #F59E0B33' }}>
                                    <b>{stand.map(p => p.playing_for.name).join(' & ')}</b> {stand.length > 1 ? 'get' : 'gets'} this game’s result.{' '}
                                    {stand.map(p => p.name).join(' & ')} {stand.length > 1 ? 'are' : 'is'} playing in their place — it doesn’t count for them.
                                  </p>
                                );
                              })()}
                              {slot.pending_confirm && (
                                <div data-testid={`pending-result-${slot.id}`}
                                  className="op-attn flex items-center justify-between gap-2 mb-2 px-2.5 py-1.5 rounded-lg flex-wrap"
                                  style={{ background: '#F59E0B14', border: '1px solid #F59E0B44' }}>
                                  <span className="text-xs font-bold" style={{ color: '#F59E0B' }}>
                                    Result waiting: {((slot.pending_winner === 'a' ? taPlayers : tbPlayers).map(p => p.name).join(' & ')) || 'Team'} won{' '}
                                    {Math.max(slot.score_a || 0, slot.score_b || 0)}–{Math.min(slot.score_a || 0, slot.score_b || 0)}
                                  </span>
                                  <button data-testid={`confirm-pending-${slot.id}`}
                                    disabled={actionLoading === `declare-${slot.id}`}
                                    onClick={() => confirmPending(slot)}
                                    className="text-xs px-2.5 py-1 rounded font-black uppercase"
                                    style={{ background: '#F59E0B', color: '#0A0A0A' }}>
                                    Confirm
                                  </button>
                                </div>
                              )}
                              {/* actions: results up front, rare ones under ⋯ */}
                              <div className="grid gap-2 mt-1" style={{ gridTemplateColumns: '1fr 1fr auto auto' }}>
                                <button data-testid={`win-a-${slot.id}`}
                                  disabled={actionLoading === `declare-${slot.id}`}
                                  onClick={() => askCompleteMatch(slot, 'a')}
                                  className="py-2.5 rounded-lg text-sm font-extrabold active:scale-[0.97]"
                                  style={{ background: '#E639460E', color: '#FF6B76', border: '1px solid #E6394655', transition: 'transform 160ms cubic-bezier(0.23,1,0.32,1)' }}>
                                  A wins
                                </button>
                                <button data-testid={`win-b-${slot.id}`}
                                  disabled={actionLoading === `declare-${slot.id}`}
                                  onClick={() => askCompleteMatch(slot, 'b')}
                                  className="py-2.5 rounded-lg text-sm font-extrabold active:scale-[0.97]"
                                  style={{ background: '#3B82F60E', color: '#6EA8FF', border: '1px solid #3B82F655', transition: 'transform 160ms cubic-bezier(0.23,1,0.32,1)' }}>
                                  B wins
                                </button>
                                {canScore && scorerUrl ? (
                                  <button data-testid={`slot-scorer-${slot.id}`}
                                    onClick={() => setScorerModal({ slot, scorerUrl })}
                                    title="Open the scorer / show its QR"
                                    className="px-3 rounded-lg text-sm font-bold inline-flex items-center gap-1"
                                    style={{ background: '#161616', color: '#E5E5E5', border: '1px solid #2A2A2A' }}>
                                    <QrCode className="h-3.5 w-3.5" /> Score
                                  </button>
                                ) : <span />}
                                <MoreMenu compact testid={`slot-more-${slot.id}`} groups={[{ title: 'This game', items: [
                                  { label: 'Replace player…', testid: `replace-${slot.id}`, icon: ArrowLeftRight,
                                    hint: started ? 'Mid-game swap — allowed, not recommended' : 'Someone can’t play — pick who steps in, and whether they come back',
                                    onClick: () => setReplaceModal({ slot, courtName: court.court_name }) },
                                  { label: 'Void game…', testid: `void-${slot.id}`, icon: X,
                                    disabled: actionLoading === `void-${slot.id}`, hint: 'No result for anyone; players go back to the queue',
                                    onClick: () => askVoid(slot) },
                                ] }]} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── TBA lane: matches called but waiting for a court ── */}
              {tbaSlots.length > 0 && (() => {
                const idleCourts = courtQueues.filter(c => (c.slots || []).length === 0 && !c.paused);
                const multiDiv = (session.divisions || []).length > 1;
                return (
                  <div className="op-attn rounded-xl overflow-hidden" data-testid="tba-lane"
                    style={{ background: '#141414', border: '1px solid #F59E0B44' }}>
                    <div className="px-4 py-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5"
                      style={{ background: '#1C1C1C', borderBottom: '1px solid #262626' }}>
                      <span className="text-xs font-bold uppercase tracking-wide whitespace-nowrap"
                        style={{ fontFamily: 'Oswald, sans-serif', color: '#F59E0B' }}>
                        TBA — called, waiting for a court
                      </span>
                      <span className="text-[10px]" style={{ color: '#525252' }}>
                        Flows onto the next court that frees up
                      </span>
                    </div>
                    <div className="p-3 space-y-2">
                      {tbaSlots.map(slot => (
                        <div key={slot.id} data-testid={`tba-slot-${slot.id}`} className="p-3 rounded-lg"
                          style={{ background: '#0A0A0A', border: '1px solid #F59E0B22' }}>
                          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase"
                              style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B30' }}>
                              {slot.set_number ? `Set ${slot.set_number}` : 'Match'} · TBA
                              {multiDiv && divisionMap[slot.division_id] ? ` · ${divisionMap[slot.division_id].name}` : ''}
                            </span>
                            {(slot.score_a > 0 || slot.score_b > 0) && (
                              <span data-testid={`tba-score-${slot.id}`} className="text-xs font-black tabular-nums"
                                style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
                                {slot.score_a}–{slot.score_b}
                              </span>
                            )}
                            {slot.eta_minutes != null && (
                              <span className="text-[10px]" style={{ color: '#A3A3A3' }}>
                                {slot.eta_minutes === 0 ? 'court free now' : `court in ~${slot.eta_minutes} min`}
                              </span>
                            )}
                            <div className="flex gap-1.5 flex-wrap justify-end">
                              <button data-testid={`tba-requeue-${slot.id}`}
                                disabled={actionLoading === `void-${slot.id}`}
                                onClick={() => askVoid(slot, 'requeue')}
                                className="text-xs px-2 py-1 rounded font-bold"
                                style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                                Back to queue
                              </button>
                              {idleCourts.filter(c => c.division_id === slot.division_id).length === 0 ? (
                                <span className="text-[10px]" style={{ color: '#525252' }}>
                                  No free {multiDiv && divisionMap[slot.division_id] ? `${divisionMap[slot.division_id].name} ` : ''}court yet
                                </span>
                              ) : idleCourts.filter(c => c.division_id === slot.division_id).map(c => (
                                <button key={c.court_id} data-testid={`tba-assign-${slot.id}-${c.court_id}`}
                                  disabled={actionLoading === `assign-${slot.id}`}
                                  onClick={() => assignCourt(slot.id, c.court_id)}
                                  className="text-xs px-2 py-1 rounded font-bold"
                                  style={{ background: '#161616', color: '#E5E5E5', border: '1px solid #2A2A2A' }}>
                                  → {c.court_name}
                                </button>
                              ))}
                            </div>
                          </div>
                          <TeamMatchup taPlayers={slot.team_a_players || []} tbPlayers={slot.team_b_players || []} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ── Up next: the queue and its main action together ── */}
              {session.status === 'active' && (
                <div className="flex flex-wrap items-center gap-2 pt-2" data-testid="op-upnext-head">
                  <h3 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>Up next</h3>
                  <span className="text-xs" style={{ color: '#737373' }}>Courts take the next match by themselves</span>
                  <div className="flex-1" />
                  <HeaderBtn icon={Flag} testid="op-last-game" label="Last game"
                    title="A player is leaving after their next game — bump it to the front"
                    onClick={() => setLastModal(true)} />
                  <HeaderBtn icon={Swords} testid="op-make-match" label="Make a match"
                    title="Pick four players and their teams — front or end of the queue"
                    onClick={() => setMakeModal(true)} />
                  <HeaderBtn variant="primary" icon={RefreshCw} testid="op-courts-generate"
                    label={generatingSet === 'open_play' ? 'Generating…' : 'Generate Rotation Set'}
                    disabled={generatingSet !== null} onClick={() => startGenerateSet('open_play')} />
                </div>
              )}

              {/* ── Pool-low prompts (JIT set generation) ── */}
              {session.status === 'active' && (session.divisions || []).map(d => {
                const divPool = poolSlots.filter(s => s.division_id === d.id);
                const divOpen = openGames.filter(g => g.division_id === d.id).length;
                const courtCount = divisionState[d.id] ? divisionState[d.id].courts : (d.court_ids || []).length;
                const multiDiv = (session.divisions || []).length > 1;
                const st = divisionState[d.id];
                // Server facts (R3-16/R3-23): prompt only when the server will
                // accept a set, sized to the matches that can really run, and —
                // unless the queue is empty — only after a game has finished
                // since the last set (late check-ins make that set too).
                const capacity = st ? st.capacity : courtCount;
                const full = st && st.pending_sets >= st.max_pending_sets;
                const short = st && st.players_here < st.players_per_match && !st.filler_ready;
                const queued = divPool.length + divOpen;
                const low = queued === 0 || (queued <= capacity && (!st || st.finished_since_last_set));
                const unusedCourts = st && st.players_here > 0 ? courtCount - capacity : 0;
                const notes = [];
                if (unusedCourts > 0 && !short) notes.push(`Only ${st.players_here} checked in — ${unusedCourts} court${unusedCourts === 1 ? '' : 's'} can't be used.`);
                if (full && st.unscheduled >= st.players_per_match) notes.push(`${st.unscheduled} checked-in player${st.unscheduled === 1 ? " isn't" : "s aren't"} in a queued set yet — they join the next set once the queue runs lower.`);
                if (short) notes.push(`${st.players_here} checked in — need at least ${st.players_per_match} for a match.`
                  + ((session.divisions || []).length > 1 && st.players_here > 0 ? ' Tap Fill this court on an empty court to borrow a player from another division.' : ''));
                const showPrompt = !full && !short && low;
                if (!showPrompt && notes.length === 0) return null;
                if (!showPrompt) {
                  return (
                    <div key={d.id} data-testid={`pool-note-${d.id}`}
                      className="mt-4 px-4 py-2.5 rounded-xl text-xs"
                      style={{ background: 'var(--sp-surface, rgba(255,255,255,0.03))', border: '1px solid rgba(148,163,184,0.25)', color: '#94A3B8' }}>
                      {multiDiv ? `${d.name}: ` : ''}{notes.join(' ')}
                    </div>
                  );
                }
                return (
                  <div key={d.id} data-testid={`pool-low-banner-${d.id}`}
                    className="op-amber-soft mt-4 flex items-center justify-between gap-3 px-4 py-3 rounded-xl flex-wrap"
                    style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid #F59E0B30' }}>
                    <p className="text-xs font-semibold" style={{ color: '#F59E0B' }}>
                      {queued === 0 ? 'Queue empty' : `Only ${queued} match${queued === 1 ? '' : 'es'} left in the queue`}
                      {multiDiv ? ` — ${d.name}` : ''}. Generate the next set to keep courts busy.
                      {notes.length > 0 && <span className="block mt-1 font-normal" style={{ color: '#94A3B8' }}>{notes.join(' ')}</span>}
                    </p>
                    {multiDiv && <button data-testid={`pool-low-generate-${d.id}`}
                      onClick={() => generateSet('open_play', {}, d.id)}
                      disabled={generatingSet !== null}
                      className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-black uppercase"
                      style={{ background: '#F59E0B', color: '#000', fontFamily: 'Oswald, sans-serif', opacity: generatingSet ? 0.6 : 1 }}>
                      {generatingSet ? 'Generating…' : `Generate for ${d.name}`}
                    </button>}
                  </div>
                );
              })}

              {/* ── Up Next: shared FIFO queue head per division ── */}
              <UpNextQueue poolSlots={poolSlots} openGames={openGames} divisions={session.divisions || []}
                busyOn={(() => {
                  // who is in a game right now → where (a queued match waits for them)
                  const at = {};
                  const mark = (sl, where) => [...(sl.team_a || []), ...(sl.team_b || []),
                    ...Object.values(sl.stand_ins || {})].forEach(pid => { at[pid] = where; });
                  courtQueues.forEach(c => (c.slots || []).filter(sl => sl.status === 'active').forEach(sl => mark(sl, c.court_name)));
                  tbaSlots.forEach(sl => mark(sl, 'a called game'));
                  return at;
                })()}
                onCall={callSlot} onSkip={(slot) => askVoid(slot)} onMove={moveSlot} actionLoading={actionLoading}
                onScore={(slot) => setScorerModal({ slot, scorerUrl: `${window.location.origin}/sports/score/${slot.qr_token}` })} />

              {/* ── Set Schedule Table: full-width, separated from court cards ── */}
              <SetScheduleTable poolSlots={poolSlots} openGames={openGames} divisions={session.divisions || []}
                onMove={session.status === 'active' ? moveSlot : null} actionLoading={actionLoading} />
              </>
            )}

            {/* ── Recent Results (bottom) — safety net for a wrongly tapped Win ── */}
            {recentResults.length > 0 && (
              <div className="op-card rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid #262626' }}>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 px-4 py-2.5" style={{ background: '#1C1C1C', borderBottom: '1px solid #262626' }}>
                  <span className="text-xs font-bold uppercase tracking-wide whitespace-nowrap" style={{ fontFamily: 'Oswald, sans-serif', color: '#A3A3A3' }}>
                    Recent Results
                  </span>
                  <span className="text-[10px]" style={{ color: '#525252' }}>
                    Wrong score or winner? Fix it until those players play again.
                  </span>
                </div>
                <div className="divide-y" style={{ borderColor: '#1A1A1A' }}>
                  {recentResults.map(s => (
                    <div key={s.id} className="grid items-center gap-2 pr-4" data-testid={`recent-result-${s.id}`}
                      style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                      <MatchupCard variant="completed" winner={s.winner}
                        teamA={s.team_a_players || []} teamB={s.team_b_players || []}
                        scoreA={s.score_a} scoreB={s.score_b} />
                      {/* owner controls live in a right rail — never inside the matchup */}
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-[10px]" style={{ color: '#525252' }}>
                          {[s.court_name, s.set_number ? `Set ${s.set_number}` : null].filter(Boolean).join(' · ')}
                        </span>
                        {s.result_corrected_at ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: '#F59E0B18', color: '#F59E0B' }}>CORRECTED</span>
                        ) : (
                          <button data-testid={`correct-result-${s.id}`}
                            disabled={actionLoading === `correct-${s.id}`}
                            onClick={() => askCorrectResult(s)}
                            className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                            style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B33' }}>
                            {actionLoading === `correct-${s.id}` ? 'Fixing…' : 'Correct result'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab content */}
        {tab === 'roster' && (
          <div className="space-y-2">
            {(() => {
              const eligible = players.filter(p => p.status === 'registered'
                && (['paid', 'free'].includes(p.payment_status) || balanceOf(p) <= 0));
              const sessionOpen = ['active', 'registration_open'].includes(session.status);
              if (players.length === 0 || session.status === 'ended') return null;
              const ready = sessionOpen && eligible.length > 0;
              if (!ready) {
                const why = !sessionOpen
                  ? 'available once registration opens or the session starts'
                  : 'no settled players waiting — everyone paid is already checked in';
                return (
                  <div data-testid="op-checkin-all-paid-disabled"
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold uppercase mb-2"
                    style={{ background: '#141414', color: '#525252',
                             border: '1px dashed #2A2A2A', fontFamily: 'Oswald, sans-serif' }}>
                    <UserCheck className="h-4 w-4" />
                    Check in all paid — {why}
                  </div>
                );
              }
              return (
                <button data-testid="op-checkin-all-paid"
                  onClick={() => setConfirmModal({
                    title: 'Check In All Paid Players',
                    color: '#22C55E',
                    confirmLabel: `Check in ${eligible.length}`,
                    message: `Check in ${eligible.length} player${eligible.length === 1 ? '' : 's'} who are fully settled (paid, comped, or free)? Players still owing, paused, or flagged are not touched.`,
                    onConfirm: async () => {
                      setConfirmModal(null);
                      try {
                        const r = await sportsApi.put(`/sports/open-play/${id}/players/check-in-paid`);
                        toast.success(`Checked in ${r.data.checked_in} player${r.data.checked_in === 1 ? '' : 's'}`
                          + (r.data.skipped_unpaid ? ` — ${r.data.skipped_unpaid} still owe and were skipped` : ''));
                        await fetchAll();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Bulk check-in failed');
                      }
                    },
                  })}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-black uppercase mb-2"
                  style={{ background: 'rgba(34,197,94,0.12)', color: '#22C55E',
                           border: '1px solid #22C55E44', fontFamily: 'Oswald, sans-serif' }}>
                  <UserCheck className="h-4 w-4" />
                  Check in all paid ({eligible.length})
                </button>
              );
            })()}
            {players.length === 0 ? (
              <div className="text-center py-12 rounded-xl" style={{ background: '#141414', border: '1px solid #262626' }}>
                <Users className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
                <p style={{ color: '#737373' }}>No players registered yet</p>
              </div>
            ) : (
              players.map(p => (
                <div key={p.id} data-testid={`player-row-${p.id}`}
                  className="flex items-center justify-between p-3 rounded-lg transition-all"
                  style={{ background: '#141414', border: '1px solid #1C1C1C' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt={p.name}
                        data-testid={`avatar-${p.id}`}
                        className="h-8 w-8 rounded-full object-cover"
                        style={{ border: `2px solid ${STATUS_COLORS[p.status]}55` }} />
                    ) : (
                      <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ background: `${STATUS_COLORS[p.status]}22`, color: STATUS_COLORS[p.status] }}>
                        {p.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold truncate" style={{ color: '#fff' }}>{p.name}</span>
                        {p.org_label && (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                            style={{ background: '#3B82F618', color: '#3B82F6' }}>{p.org_label}</span>
                        )}
                        <StatusBadge status={p.status} />
                        <PayBadge status={p.payment_status} />
                        {p.gave_spot_to && (
                          <span data-testid={`gave-spot-${p.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: '#FFFFFF0A', color: '#A3A3A3', border: '1px solid #333' }}>
                            Gave spot to {p.gave_spot_to.name}
                          </span>
                        )}
                        {p.took_spot_from && (
                          <span data-testid={`took-spot-${p.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: '#22C55E14', color: '#4ADE80', border: '1px solid #22C55E44' }}>
                            Took {p.took_spot_from.name}'s spot
                          </span>
                        )}
                      </div>
                      <div className="text-xs flex items-center gap-2 flex-wrap" style={{ color: '#555' }}>
                        {p.phone && !String(p.phone).startsWith('rsvp-') && <span>{p.phone}</span>}
                        {p.email && String(p.phone || '').startsWith('rsvp-') && <span>{p.email}</span>}
                        {divisionMap[p.division_id] && (
                          <span style={{ color: '#737373' }}>{divisionMap[p.division_id].name}</span>
                        )}
                        {p.matches_played > 0 && (
                          <span>{p.matches_played}P · {p.wins}W</span>
                        )}
                        {p.level != null && (
                          <span data-testid={`level-${p.id}`} className="inline-flex items-center gap-1"
                            title="Internal level (1–10) — starts from the skill bar / DUPR, moves with results. Players never see it.">
                            <span style={{ color: '#A3A3A3' }}>Lv {p.level}</span>
                            {session.status !== 'ended' && (
                              <>
                                <button data-testid={`level-up-${p.id}`} onClick={() => nudgeLevel(p, 1)}
                                  disabled={actionLoading === `level-${p.id}` || p.level >= 10}
                                  className="px-1 rounded leading-none" title="Raise level by 1"
                                  style={{ color: '#22C55E', border: '1px solid #22C55E44', opacity: p.level >= 10 ? 0.35 : 1 }}>▲</button>
                                <button data-testid={`level-down-${p.id}`} onClick={() => nudgeLevel(p, -1)}
                                  disabled={actionLoading === `level-${p.id}` || p.level <= 1}
                                  className="px-1 rounded leading-none" title="Lower level by 1"
                                  style={{ color: '#EF4444', border: '1px solid #EF444444', opacity: p.level <= 1 ? 0.35 : 1 }}>▼</button>
                              </>
                            )}
                          </span>
                        )}
                        {p.level_hint && session.status !== 'ended' && (
                          <span data-testid={`level-hint-${p.id}`} style={{ color: '#F59E0B' }}>{p.level_hint.message}</span>
                        )}
                        {p.bench_streak > 0 && (
                          <span style={{ color: '#F59E0B' }}>bench: {p.bench_streak}</span>
                        )}
                        <button data-testid={`gender-${p.id}`}
                          onClick={() => setPlayerGender(p.id, p.gender === 'M' ? 'F' : p.gender === 'F' ? '' : 'M')}
                          title="Gender for early-round balancing — tap to cycle M / F / none"
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase leading-none"
                          style={p.gender === 'M'
                            ? { background: '#3B82F622', color: '#3B82F6', border: '1px solid #3B82F644' }
                            : p.gender === 'F'
                              ? { background: '#EC489922', color: '#EC4899', border: '1px solid #EC489944' }
                              : { background: '#26262680', color: '#737373', border: '1px solid #333' }}>
                          {p.gender === 'M' ? '♂ M' : p.gender === 'F' ? '♀ F' : '+ sex'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0 flex-wrap justify-end" style={{ maxWidth: '55%' }}>
                    {session.status !== 'ended' && (session.divisions || []).length > 1
                      && !['done', 'removed'].includes(p.status) && (
                      <select data-testid={`move-${p.id}`} value=""
                        disabled={actionLoading === `move-${p.id}`}
                        onChange={e => e.target.value && askMove(p, e.target.value)}
                        title="Move to another division"
                        className="rounded-lg text-xs px-1.5"
                        style={{ background: '#0F0F0F', color: '#A3A3A3', border: '1px solid #2A2A2A', minHeight: 36, maxWidth: 110 }}>
                        <option value="">Move to…</option>
                        {session.divisions.filter(d => d.id !== p.division_id).map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    )}
                    {session.status !== 'ended' && p.status === 'waitlisted' && (
                      <SmallBtn icon={ArrowUp} label="Promote from waitlist" color="#8B5CF6"
                        testid={`promote-${p.id}`}
                        loading={actionLoading === `promote-${p.id}`}
                        onClick={() => doAction('promote', p.id)} />
                    )}
                    {p.proof_url && (
                      <SmallBtn icon={Eye} label="View payment proof" color="#3B82F6"
                        testid={`proof-${p.id}`}
                        onClick={() => setProofPlayer(p)} />
                    )}
                    {p.payment_status !== 'free' && balanceOf(p) > 0 && (
                      <button data-testid={`collect-${p.id}`}
                        onClick={() => openCollect(p)}
                        className="inline-flex items-center gap-1 px-2.5 rounded-lg text-xs font-black uppercase"
                        style={{ background: 'rgba(245,158,11,0.12)', color: C.amber,
                                 border: `1px solid ${C.amber}55`, minHeight: 36,
                                 fontFamily: 'Oswald, sans-serif' }}
                        title="Collect payment — opens the review screen">
                        <Wallet className="h-3.5 w-3.5" />
                        Collect ₱{balanceOf(p).toLocaleString()}
                      </button>
                    )}
                    {session.status !== 'ended' && (p.status === 'registered' || p.status === 'paused' || p.status === 'flagged') && (
                      <SmallBtn icon={UserCheck} label="Check In" color="#22C55E"
                        testid={`checkin-${p.id}`}
                        loading={actionLoading === `check-in-${p.id}`}
                        onClick={() => doAction('check-in', p.id)} />
                    )}
                    {session.status !== 'ended' && (p.status === 'active' || p.status === 'flagged'
                      || (p.status === 'playing' && !p.pause_requested)) && (
                      <SmallBtn icon={Pause} label={p.status === 'playing' ? 'Pause after this game' : 'Pause'} color="#F59E0B"
                        testid={`pause-${p.id}`}
                        loading={actionLoading === `pause-${p.id}`}
                        onClick={() => askPause(p)} />
                    )}
                    {session.status !== 'ended' && p.status === 'playing' && p.pause_requested && (
                      <SmallBtn icon={RotateCcw} label="Cancel pause after game" color="#A3A3A3"
                        testid={`cancel-pause-${p.id}`}
                        loading={actionLoading === `resume-${p.id}`}
                        onClick={() => doAction('resume', p.id)} />
                    )}
                    {session.status !== 'ended' && p.status === 'paused' && (
                      <SmallBtn icon={RotateCcw} label="Resume" color="#22C55E"
                        testid={`resume-${p.id}`}
                        loading={actionLoading === `resume-${p.id}`}
                        onClick={() => doAction('resume', p.id)} />
                    )}
                    {session.status !== 'ended' && p.status === 'done' && !p.gave_spot_to && (
                      <SmallBtn icon={RotateCcw} label="Reactivate — back to rotation" color="#22C55E"
                        testid={`reactivate-${p.id}`}
                        loading={actionLoading === `reactivate-${p.id}`}
                        onClick={() => doAction('reactivate', p.id)} />
                    )}
                    {session.status !== 'ended' && p.status !== 'done' && p.status !== 'waitlisted' && !p.leave_after_match && (
                      <SmallBtn icon={UserX} label="Done" color="#737373"
                        testid={`done-${p.id}`}
                        loading={actionLoading === `done-${p.id}`}
                        onClick={() => askDone(p)} />
                    )}
                    {session.status !== 'ended' && (p.matches_played || 0) === 0 && p.status !== 'playing' && (
                      <SmallBtn icon={Trash2} label="Remove entry" color="#EF4444"
                        testid={`remove-${p.id}`}
                        loading={actionLoading === `remove-${p.id}`}
                        onClick={() => removePlayer(p)} />
                    )}
                    <MoreMenu compact testid={`player-more-${p.id}`} groups={[{ title: p.name, items: [
                      { label: 'Edit name…', testid: `edit-name-${p.id}`, icon: Pencil,
                        hint: 'Fix a typo — games, level and payment stay',
                        onClick: () => setSpotModal({ kind: 'name', player: p }) },
                      session.status !== 'ended' && !p.gave_spot_to
                        && ['registered', 'waitlisted', 'active', 'paused', 'flagged', 'playing'].includes(p.status) && {
                        label: 'Give spot to someone else…', testid: `give-spot-${p.id}`, icon: UserRoundPlus,
                        hint: `${p.name} keeps their games but stops playing; a new player starts fresh`,
                        onClick: () => setSpotModal({ kind: 'give', player: p }) },
                    ] }]} />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'setup' && (
          <div className="space-y-4" data-testid="op-setup-divisions">
            <SectionTitle>Divisions</SectionTitle>
            {(session.divisions || []).map(d => {
              const divPlayers = playersByDiv[d.id] || [];
              return (
                <div key={d.id} className="op-card p-4 rounded-xl" style={{ background: '#141414', border: '1px solid #262626' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-bold uppercase" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
                        {d.name}
                      </span>
                      <span className="text-xs ml-2" style={{ color: '#737373' }}>
                        {d.player_count || divPlayers.length}/{d.hard_cap} players · {d.court_ids?.length || 0} courts
                      </span>
                    </div>
                    {d.active_count > 0 && (
                      <span className="text-xs font-bold" style={{ color: '#22C55E' }}>{d.active_count} active</span>
                    )}
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: '#0A0A0A' }}>
                    <div className="h-full rounded-full transition-all" style={{
                      width: `${Math.min(100, ((d.player_count || divPlayers.length) / (d.hard_cap || 1)) * 100)}%`,
                      background: (d.player_count || divPlayers.length) >= (d.hard_cap || 999) ? '#EF4444' : '#E63946',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Leaderboard tab — one table per division, ranked exactly like the
            podium and the public page (it used to be one merged list by wins) */}
        {tab === 'leaderboard' && (
          <div className="space-y-4">
            {!(ownerBoard || []).some(d => d.entries.length) ? (
              <div className="text-center py-12 rounded-xl" style={{ background: '#141414', border: '1px solid #262626' }}>
                <Award className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
                <p style={{ color: '#737373' }}>No matches completed yet</p>
              </div>
            ) : (ownerBoard || []).filter(d => d.entries.length).map(d => (
              <div key={d.division_id || 'all'} className="op-card rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid #262626' }}>
                {(ownerBoard || []).length > 1 && (
                  <div className="px-4 py-2 text-xs font-bold uppercase" style={{ background: '#1C1C1C', color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>
                    {d.division_name}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: '#1C1C1C' }}>
                        {(isWinsRule(d) ? ['#', 'Player', 'Played', 'W–L', 'Win%', 'Partners', 'Status']
                          : isFairRule(d) ? ['#', 'Player', 'Played', 'W–L', 'Score', 'Pts %', 'Partners', 'Status']
                          : ['#', 'Player', 'Played', 'W–L', 'Win%', 'Pts ±', 'Partners', 'Status']).map((h, i) => (
                          <th key={h} className={`${i < 2 ? 'text-left px-4' : 'text-center px-3'} py-2.5 text-xs uppercase`} style={{ color: '#737373' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.entries.map((p, i) => {
                        const fair = isFairRule(d);
                        const wins = isWinsRule(d);
                        const unranked = fair && p.rank == null;
                        const firstUnranked = unranked && (i === 0 || d.entries[i - 1].rank != null);
                        const wo = (p.walkover_wins || 0) + (p.walkover_losses || 0);
                        return (
                          <Fragment key={p.id}>
                            {firstUnranked && (
                              <tr data-testid="lb-under-minimum"><td colSpan={wins ? 7 : 8} className="px-4 py-1.5 text-[11px]"
                                style={{ background: '#101010', color: '#737373', borderTop: '1px solid #1C1C1C' }}>
                                {wins ? `Not on the podium — needs ${d.min_games}+ games and more wins than losses`
                                  : `Under ${d.min_games} games — not on the podium`}
                              </td></tr>
                            )}
                            <tr data-testid={`lb-row-${p.id}`} style={{ borderTop: '1px solid #1C1C1C', opacity: unranked ? 0.55 : 1 }}>
                              <td className="px-4 py-2 font-bold" style={{ color: p.rank && p.rank <= 3 ? '#F59E0B' : '#555' }}>{p.rank ?? '–'}</td>
                              <td className="px-4 py-2 font-bold" style={{ color: '#fff' }}>
                                {p.name}
                                {fair && wo > 0 && <span className="ml-1.5 text-[10px] font-semibold" style={{ color: '#737373' }}
                                  title="Walkovers are shown apart and don't count in the podium maths">
                                  +{p.walkover_wins ? 'W/O win' : 'W/O loss'}{wo > 1 ? ` ×${wo}` : ''}</span>}
                              </td>
                              <td className="text-center px-3 py-2" style={{ color: '#A3A3A3' }}>{p.matches_played}</td>
                              <td className="text-center px-3 py-2 font-bold" style={{ color: '#22C55E' }}>{p.wins}–{p.losses}</td>
                              <td className="text-center px-3 py-2" style={{ color: '#A3A3A3' }}
                                title={fair && !wins ? 'Win rate with a small bonus for games played: (wins + 1) ÷ (games + 2)' : undefined}>
                                {fair && !wins ? `${Math.round(p.podium_score)}%` : `${p.win_rate}%`}</td>
                              {!wins && <td className="text-center px-3 py-2" style={{ color: '#A3A3A3' }}>
                                {fair ? `${p.points_ratio}%` : (p.point_diff > 0 ? `+${p.point_diff}` : p.point_diff)}</td>}
                              <td className="text-center px-3 py-2" style={{ color: '#A3A3A3' }}>{p.unique_partners}</td>
                              <td className="text-center px-3 py-2"><StatusBadge status={p.status} /></td>
                            </tr>
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {isFairRule(d) && <div className="px-4 py-3" style={{ borderTop: '1px solid #1C1C1C' }}><HowRanked minGames={d.min_games} rule={d.podium_rule} tone="#F59E0B" /></div>}
              </div>
            ))}
          </div>
        )}

        {/* Setup, continued: QR codes + invitations */}
        {tab === 'setup' && (
          <div className="mt-8" data-testid="op-setup-qr">
            <SectionTitle>QR codes</SectionTitle>
            <OpenPlayQRHub session={session} sessionId={id} slug={organization?.slug} courtQueues={courtQueues} />
          </div>
        )}
        {tab === 'setup' && (
          <div className="mt-8" data-testid="op-setup-invitations">
            <SectionTitle>Invitations</SectionTitle>
          <OpenPlayInvitationsPanel
            sessionId={id}
            session={session}
            invitations={invitations}
            onRefresh={fetchAll}
          />
          </div>
        )}
      </div>

      {addModal && (
        <AddPlayerModal session={session} players={players} onClose={() => setAddModal(false)}
          onAdded={() => { setAddModal(false); fetchAll(); }} sessionId={id} />
      )}
      {proofPlayer && (
        <ProofModal player={proofPlayer} onClose={() => setProofPlayer(null)}
          onCollect={() => openCollect(proofPlayer,
            { amount: balanceOf(proofPlayer), method: 'gcash' })} />
      )}
      {reconcile && (
        <PaymentReconcileModal
          mode="open_play"
          eventId={id}
          players={reconcile.players}
          playerIndex={reconcile.index}
          initial={reconcile.initial}
          onSendLink={(p) => setPaylinkPlayer(p)}
          onClose={() => setReconcile(null)}
          onSaved={async () => { await fetchAll(); }}
        />
      )}
      <PaymentLinkModal
        open={!!paylinkPlayer}
        onClose={() => setPaylinkPlayer(null)}
        title={paylinkPlayer ? `Send Link — ${paylinkPlayer.name}` : ''}
        subtitle={paylinkPlayer ? `${session?.name || 'Session'}${paylinkPlayer.division_name ? ` · ${paylinkPlayer.division_name}` : ''}` : ''}
        baseAmount={paylinkPlayer?.amount_due || session?.entry_fee || 0}
        onGenerate={async ({ amount_choice, amount_php }) => {
          const r = await sportsApi.post(
            `/sports/open-play/${id}/players/${paylinkPlayer.id}/payment-link`,
            { amount_choice, amount_php }
          );
          fetchAll();
          return r.data;
        }} />
      {pinPrompt && (
        <PinPromptModal onSubmit={pinPrompt.onSubmit} onCancel={pinPrompt.onCancel} error={pinPrompt.error} />
      )}
      {scorerModal && (
        <ScorerLaunchModal slot={scorerModal.slot} scorerUrl={scorerModal.scorerUrl} onClose={() => setScorerModal(null)} />
      )}
      {addCourtModal && (
        <AddCourtModal sessionId={id} divId={addCourtModal.divId} divName={addCourtModal.divName}
          onClose={() => setAddCourtModal(null)} onAdded={() => { setAddCourtModal(null); fetchAll(); }} />
      )}
      {skillSetModal && (
        <SkillSetModal
          onClose={() => setSkillSetModal(false)}
          onGenerate={(opts) => { setSkillSetModal(false); generateSet('skill_based', opts, setDivision); }}
          isGenerating={generatingSet === 'skill_based'}
        />
      )}
      {divPickerType && (
        <DivisionPickerModal
          divisions={session.divisions || []}
          setType={divPickerType}
          onClose={() => setDivPickerType(null)}
          onPick={(divId) => {
            const t = divPickerType;
            setDivPickerType(null);
            if (t === 'skill_based') { setSetDivision(divId); setSkillSetModal(true); }
            else generateSet('open_play', {}, divId);
          }}
        />
      )}
      {resultModal && (
        <ResultModal mode={resultModal.mode} slot={resultModal.slot} initialWinner={resultModal.winner}
          needsPin={!!session?.has_scorer_pin}
          busy={actionLoading === `${resultModal.mode}-${resultModal.slot.id}`}
          onCancel={() => setResultModal(null)}
          onSubmit={(body) => submitResult(resultModal.mode, resultModal.slot, body)} />
      )}

      {onCourtChoice && (
        <ConfirmModal title="On court right now" color="#EF4444" hideConfirm
          onConfirm={() => setOnCourtChoice(null)} onCancel={() => setOnCourtChoice(null)}
          message={(
            <>
              <b style={{ color: '#fff' }}>{onCourtChoice.player.name}</b> is playing
              {onCourtChoice.detail.court_name ? ` on ${onCourtChoice.detail.court_name}` : ''} (score {onCourtChoice.detail.score_a}–{onCourtChoice.detail.score_b}).
              What happens to that game?
              <span className="flex flex-col gap-1.5 mt-3">
                {[
                  ['after_match', 'Leave after this game', 'They finish it and get credit; then they’re Done.'],
                  ['sub_now', 'Substitute now', 'A free player takes their place; the substitute gets the result.'],
                  ['void_match', 'Void the game', 'No result for anyone; the other players go back to the queue.'],
                ].map(([key, label, hint]) => (
                  <button key={key} data-testid={`oncourt-${key}`}
                    onClick={() => { const pl = onCourtChoice.player; setOnCourtChoice(null); doAction('done', pl.id, { on_court: key }); }}
                    className="text-left px-3 py-2 rounded-lg"
                    style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                    <span className="block text-sm font-bold" style={{ color: '#fff' }}>{label}</span>
                    <span className="block text-[11px]" style={{ color: '#737373' }}>{hint}</span>
                  </button>
                ))}
              </span>
            </>
          )} />
      )}

      {courtPauseChoice && (
        <ConfirmModal title={`Pause ${courtPauseChoice.court.court_name}`} color="#F59E0B"
          hideConfirm={!!courtPauseChoice.live} confirmLabel="Pause court"
          onConfirm={() => pauseCourt(courtPauseChoice.court, false)}
          onCancel={() => setCourtPauseChoice(null)}
          message={courtPauseChoice.live ? (
            <>
              A game is on this court
              {((courtPauseChoice.live.score_a || 0) + (courtPauseChoice.live.score_b || 0)) > 0
                ? ` (score ${courtPauseChoice.live.score_a || 0}–${courtPauseChoice.live.score_b || 0})` : ''}.
              The court takes no new matches until you reopen it.
              <span className="flex flex-col gap-1.5 mt-3">
                {[
                  [false, 'Pause after this game', 'The game finishes normally, then the court stops.'],
                  [true, 'Pause now — game back to the front of the queue', 'Players leave the court now; the game keeps its score and resumes on the next free court.'],
                ].map(([sendLive, label, hint]) => (
                  <button key={label} data-testid={`court-pause-${sendLive ? 'now' : 'after'}`}
                    onClick={() => pauseCourt(courtPauseChoice.court, sendLive)}
                    className="text-left px-3 py-2 rounded-lg"
                    style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                    <span className="block text-sm font-bold" style={{ color: '#fff' }}>{label}</span>
                    <span className="block text-[11px]" style={{ color: '#737373' }}>{hint}</span>
                  </button>
                ))}
              </span>
            </>
          ) : 'The court takes no new matches until you reopen it. The queue keeps running on the other courts.'} />
      )}

      {endModal && (
        <EndSessionModal matches={endModal.matches} busy={endModal.busy}
          onCancel={() => setEndModal(null)} onSubmit={submitEndSession} />
      )}

      {podium && (
        <OpenPlayPodiumModal
          podium={podium}
          session={session}
          organization={organization}
          onClose={() => setPodium(null)}
        />
      )}
    </div>
  );
}

// ─────────── Open Play End-of-Session Podium ───────────
const OP_TIERS = {
  1: { color: '#FFD54A', glow: '#FFD54A', label: 'Champion', h: 118, size: 76 },
  2: { color: '#CBD5E1', glow: '#94A3B8', label: 'Runner-up', h: 86, size: 60 },
  3: { color: '#E0A86B', glow: '#CD7F32', label: '3rd Place', h: 66, size: 56 },
};

function PodiumStep({ place, player, fair }) {
  if (!player) return null;
  const t = OP_TIERS[place];
  return (
    <div className="flex flex-col items-center" style={{ width: place === 1 ? 130 : 110 }}>
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <div style={{
          width: t.size, height: t.size, borderRadius: '50%', overflow: 'hidden',
          border: `3px solid ${t.color}`, boxShadow: `0 0 18px ${t.glow}55`,
          background: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: place === 1 ? 28 : 22, fontWeight: 900, color: t.color, fontFamily: 'Oswald, sans-serif',
        }}>
          {player.avatar_url
            ? <img src={player.avatar_url} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : player.name?.[0]?.toUpperCase()}
        </div>
        {place === 1 && <Crown style={{ position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)', width: 24, height: 24, color: '#FFD54A' }} />}
        <span style={{ position: 'absolute', bottom: -6, right: -6 }}>
          <MedalBadge rank={place} size={22} />
        </span>
      </div>
      {/* A shared place (tie) wraps every name instead of truncating them. */}
      <p className={`text-center font-bold ${player.tied ? '' : 'truncate'}`} style={{ color: '#fff', fontSize: place === 1 ? 14 : 13, maxWidth: place === 1 ? 128 : 108 }}>{player.name}</p>
      <p className="text-center" style={{ color: t.color, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
        {player.tied ? (place === 1 ? 'Co-champions' : `Tied ${t.label}`) : t.label}
      </p>
      <p className="text-center text-xs mt-0.5" style={{ color: '#737373' }}>
        {fair && !player.tied ? fairRecord(player) : `${player.wins} won · ${player.matches_played} played`}
      </p>
      <div style={{
        marginTop: 8, width: place === 1 ? 110 : 92, height: t.h, borderRadius: '8px 8px 0 0',
        background: `linear-gradient(180deg, ${t.color}33, ${t.color}10)`,
        border: `1px solid ${t.color}55`, borderBottom: 'none',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 8,
      }}>
        <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: place === 1 ? 34 : 26, fontWeight: 900, color: t.color }}>{place}</span>
      </div>
    </div>
  );
}

// Declare or correct a result by the final score (flip-card courts): the
// winner follows the score, ties are refused, a walkover is explicit.
// Tapping a score box selects what is in it, so typing replaces the 0
// instead of adding to it (owner 2026-10-01).
const selectScore = (e) => { const el = e.currentTarget; requestAnimationFrame(() => el.select()); };

function ResultModal({ mode, slot, initialWinner, needsPin = false, busy, onCancel, onSubmit }) {
  const backdrop = useBackdropClose(busy ? () => {} : onCancel);
  const nameA = (slot.team_a_players || []).map(p => p.name).join(' & ') || 'Team A';
  const nameB = (slot.team_b_players || []).map(p => p.name).join(' & ') || 'Team B';
  const [a, setA] = useState(String(slot.score_a ?? 0));
  const [b, setB] = useState(String(slot.score_b ?? 0));
  const [walkover, setWalkover] = useState(false);
  const [woWinner, setWoWinner] = useState(initialWinner || 'a');
  const [woPin, setWoPin] = useState('');
  const [err, setErr] = useState('');
  const ok = (v) => /^\d{1,3}$/.test(String(v).trim());
  const na = parseInt(a, 10), nb = parseInt(b, 10);
  const scoreErr = !ok(a) || !ok(b) ? 'Enter both scores (whole numbers)' : na === nb ? 'Tied — a game needs a winner' : '';
  const winner = na > nb ? 'a' : 'b';
  // Tapped "A wins" but typed a score where B leads (or the other way): the score
  // decides — say so plainly before saving (owner 2026-10-01).
  const mismatch = mode === 'declare' && !walkover && !scoreErr && !!initialWinner && winner !== initialWinner;
  const pinOk = !needsPin || /^\d{4,6}$/.test(woPin.trim());
  const canSubmit = !busy && (walkover ? mode === 'declare' && pinOk : !scoreErr);
  const submit = async () => {
    if (!canSubmit) return;
    setErr('');
    const body = walkover ? { winner: woWinner, walkover: true, ...(needsPin ? { pin: woPin.trim() } : {}) }
      : mode === 'declare' ? { winner, score_a: na, score_b: nb } : { score_a: na, score_b: nb };
    const e = await onSubmit(body);
    if (e) setErr(e);
  };
  const flip = async () => {  // correct mode: 'wrong team tapped / sides swapped'
    setErr('');
    const e = await onSubmit({});
    if (e) setErr(e);
  };
  const input = { background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8,
    padding: '8px', width: 64, textAlign: 'center', fontSize: 20, fontWeight: 800, outline: 'none' };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}
      {...backdrop} data-testid="op-result-modal">
      <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}
        onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-bold uppercase mb-1" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
          {mode === 'declare' ? 'Enter result' : 'Correct result'}
        </h3>
        <p className="text-xs mb-4" style={{ color: '#737373' }}>
          {mode === 'declare' ? 'Type the final score from the court. The higher score wins.'
            : 'Type the correct final score. Wins, losses and ratings follow it.'}
        </p>
        {!walkover && (
          <div className="space-y-2 mb-3">
            {[[nameA, a, setA, '#E63946', 'a'], [nameB, b, setB, '#3B82F6', 'b']].map(([nm, v, set, color, side]) => (
              <div key={side} className="flex items-center justify-between gap-3">
                <span className="text-sm font-bold truncate" style={{ color }}>{nm}</span>
                <input data-testid={`result-score-${side}`} inputMode="numeric" value={v} disabled={busy}
                  onFocus={selectScore} onClick={selectScore}
                  onChange={e => set(e.target.value)} style={input} />
              </div>
            ))}
            {mismatch ? (
              <p data-testid="result-mismatch" className="text-xs leading-relaxed px-3 py-2 rounded-lg"
                style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' }}>
                You tapped <b style={{ color: '#FCD34D' }}>{initialWinner.toUpperCase()} wins</b>, but{' '}
                <b style={{ color: '#FCD34D' }}>{winner.toUpperCase()}</b> has the higher score — saving gives the win to{' '}
                <b style={{ color: winner === 'a' ? '#FF6B76' : '#6EA8FF' }}>{winner === 'a' ? nameA : nameB}</b>{' '}
                ({Math.max(na, nb)}–{Math.min(na, nb)}).
              </p>
            ) : (
              <p className="text-xs" style={{ color: scoreErr ? '#EF4444' : '#22C55E' }}>
                {scoreErr || `${winner === 'a' ? nameA : nameB} win ${Math.max(na, nb)}–${Math.min(na, nb)}`}
              </p>
            )}
          </div>
        )}
        {mode === 'declare' && (
          <label className="flex items-start gap-2 mb-3 cursor-pointer text-xs" style={{ color: '#A3A3A3' }}>
            <input type="checkbox" data-testid="result-walkover" checked={walkover} disabled={busy}
              onChange={e => setWalkover(e.target.checked)} className="mt-0.5" />
            <span>Walkover — a team can't play (automatic win)</span>
          </label>
        )}
        {walkover && (
          <div className="flex gap-2 mb-3">
            {[['a', nameA, '#E63946'], ['b', nameB, '#3B82F6']].map(([side, nm, color]) => (
              <button key={side} onClick={() => setWoWinner(side)} data-testid={`result-wo-${side}`}
                className="flex-1 px-2 py-2 rounded-lg text-xs font-bold truncate"
                style={woWinner === side ? { background: `${color}22`, color, border: `1px solid ${color}66` }
                  : { background: 'transparent', color: '#525252', border: '1px solid #262626' }}>
                {nm} win
              </button>
            ))}
          </div>
        )}
        {walkover && (
          <div data-testid="result-wo-warning" className="rounded-lg px-3 py-2.5 mb-3 text-xs space-y-1.5"
            style={{ background: '#F59E0B12', border: '1px solid #F59E0B44', color: '#E5E5E5', lineHeight: 1.5 }}>
            <p className="font-bold" style={{ color: '#FCD34D' }}>Record a walkover?</p>
            <p>
              <b>{woWinner === 'a' ? nameA : nameB}</b> get{(woWinner === 'a' ? nameA : nameB).includes('&') ? '' : 's'} an
              {' '}<b>automatic win</b> and the game ends now. It goes on both teams' records as a walkover (W/O).
              No points are recorded. Only use this when a team can't play.
            </p>
            <p style={{ color: '#A3A3A3' }}>
              <b style={{ color: '#FCD34D' }}>Out of time?</b> Don't use walkover — End session lets you save the score or discard the game.
            </p>
            {needsPin && (
              <label className="flex items-center gap-2 pt-1">
                <span className="font-bold" style={{ color: '#FCD34D' }}>Session PIN</span>
                <input data-testid="result-wo-pin" type="password" inputMode="numeric" autoComplete="off"
                  maxLength={6} value={woPin} disabled={busy} placeholder="••••"
                  onChange={e => setWoPin(e.target.value.replace(/\D/g, ''))}
                  style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8,
                           padding: '6px 8px', width: 96, letterSpacing: 4, fontWeight: 800, outline: 'none' }} />
              </label>
            )}
          </div>
        )}
        {err && <p data-testid="result-error" role="alert" className="text-xs mb-3 font-bold" style={{ color: '#EF4444' }}>{err}</p>}
        <div className="flex gap-2 justify-end flex-wrap">
          {mode === 'correct' && (
            <button data-testid="result-flip" onClick={flip} disabled={busy}
              className="mr-auto px-3 py-2 rounded-lg text-xs font-bold"
              style={{ background: '#1A1A1A', color: '#F59E0B', border: '1px solid #F59E0B33' }}>
              Wrong team — flip winner
            </button>
          )}
          <button data-testid="result-cancel" onClick={onCancel} disabled={busy}
            className="px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
            Cancel
          </button>
          <button data-testid="result-save" onClick={submit} disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-xs font-bold uppercase"
            style={{ background: walkover || mismatch ? '#F59E0B' : '#22C55E', color: '#0A0A0A', opacity: canSubmit ? 1 : 0.5 }}>
            {busy ? 'Saving…' : walkover ? 'Record walkover'
              : mismatch ? `Save — ${winner.toUpperCase()} wins ${Math.max(na, nb)}–${Math.min(na, nb)}` : 'Save result'}
          </button>
        </div>
      </div>
    </div>
  );
}

// The console is the top authority, but taking a game a court phone is scoring
// is deliberate (owner 2026-09-30): same scorer lock + PIN override as phones.
function TakeoverModal({ detail, label, needsPin, onCancel, onConfirm }) {
  const backdrop = useBackdropClose(onCancel);
  const [pin, setPin] = useState('');
  const ok = !needsPin || /^\d{4,6}$/.test(pin);
  const ago = detail?.seconds_ago ?? 0;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}
      {...backdrop} data-testid="op-takeover-modal">
      <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#0F0F0F', border: '1px solid #F59E0B55' }}
        onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-bold uppercase mb-1" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
          Someone is scoring this game on a phone
        </h3>
        <p className="text-sm mb-1" style={{ color: '#E5E5E5' }}>
          Score <b>{detail?.score_a}–{detail?.score_b}</b> · last point {ago < 60 ? `${ago} s` : `${Math.round(ago / 60)} min`} ago
        </p>
        <p className="text-xs mb-4" style={{ color: '#A3A3A3', lineHeight: 1.5 }}>
          Taking over ends their scoring for this game — your action replaces theirs. The phone is told the organizer took over.
        </p>
        {needsPin && (
          <label className="flex items-center gap-2 mb-4 text-xs">
            <span className="font-bold" style={{ color: '#FCD34D' }}>Session PIN</span>
            <input data-testid="op-takeover-pin" type="password" inputMode="numeric" autoComplete="off" autoFocus
              maxLength={6} value={pin} placeholder="••••" onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
              style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8,
                       padding: '6px 8px', width: 96, letterSpacing: 4, fontWeight: 800, outline: 'none' }} />
          </label>
        )}
        <div className="flex gap-2 justify-end">
          <button data-testid="op-takeover-cancel" onClick={onCancel} className="px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>Cancel</button>
          <button data-testid="op-takeover-confirm" onClick={() => ok && onConfirm(pin)} disabled={!ok}
            className="px-4 py-2 rounded-lg text-xs font-bold uppercase"
            style={{ background: '#F59E0B', color: '#0A0A0A', opacity: ok ? 1 : 0.5 }}>{label}</button>
        </div>
      </div>
    </div>
  );
}

function EndSessionModal({ matches, busy, onCancel, onSubmit }) {
  const backdrop = useBackdropClose(busy ? () => {} : onCancel);
  const initRow = (m) => ({
    action: m.score_a !== m.score_b ? 'save' : 'discard',
    a: String(m.score_a ?? 0),
    b: String(m.score_b ?? 0),
  });
  const [rows, setRows] = useState(() => Object.fromEntries(matches.map(m => [m.slot_id, initRow(m)])));
  // Games added after a 409 (started while the dialog was open) get a row too.
  useEffect(() => {
    setRows(r => {
      const missing = matches.filter(m => !r[m.slot_id]);
      return missing.length ? { ...r, ...Object.fromEntries(missing.map(m => [m.slot_id, initRow(m)])) } : r;
    });
  }, [matches]);

  const setRow = (sid, patch) => setRows(r => ({ ...r, [sid]: { ...r[sid], ...patch } }));
  const parsed = (v) => (/^\d{1,3}$/.test(String(v).trim()) ? parseInt(v, 10) : null);
  const rowState = (m) => {
    const r = rows[m.slot_id] || initRow(m);
    if (r.action === 'discard') return { ok: true, r };
    const a = parsed(r.a), b = parsed(r.b);
    if (a === null || b === null) return { ok: false, r, error: 'Enter both scores (whole numbers)' };
    if (a === b) return { ok: false, r, error: 'Tied — change the score or discard this game' };
    return { ok: true, r, a, b, winner: a > b ? 'a' : 'b' };
  };
  const states = matches.map(m => ({ m, ...rowState(m) }));
  const canSubmit = !busy && states.every(x => x.ok);

  const submit = () => {
    if (!canSubmit) return;
    const finish = states.filter(x => x.r.action === 'save')
      .map(x => ({ slot_id: x.m.slot_id, winner: x.winner, score_a: x.a, score_b: x.b }));
    const discard = states.filter(x => x.r.action === 'discard').map(x => x.m.slot_id);
    onSubmit(finish, discard);
  };

  const inputStyle = { background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8,
    padding: '6px 8px', width: 56, textAlign: 'center', fontSize: 16, fontWeight: 800, outline: 'none' };
  const n = matches.length;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.85)' }} {...backdrop} data-testid="op-end-modal">
      <div className="w-full max-w-lg rounded-2xl p-5 my-auto" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2">
          <Square className="h-5 w-5 shrink-0" style={{ color: '#EF4444' }} />
          <h3 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>End Session</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: '#A3A3A3', lineHeight: 1.5 }}>
          {n > 0
            ? <><b style={{ color: '#fff' }}>{n} game{n === 1 ? ' is' : 's are'} still being played.</b> Save the result of each game, or discard it.</>
            : 'No games are being played right now.'}
          <span className="block mt-1 text-xs" style={{ color: '#737373' }}>
            Ending marks everyone as done and locks the final standings. This can't be undone.
          </span>
        </p>

        {n > 0 && (
          <div className="space-y-2.5 mb-4 max-h-[55vh] overflow-y-auto pr-1">
            {states.map(({ m, r, error, winner }) => (
              <div key={m.slot_id} data-testid={`op-end-row-${m.slot_id}`} className="rounded-xl p-3"
                style={{ background: '#0A0A0A', border: `1px solid ${r.action === 'save' ? '#2A2A2A' : '#1C1C1C'}` }}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[11px] font-bold uppercase" style={{ color: '#737373' }}>
                    {m.court_name || 'Waiting for a court'}
                  </span>
                  {m.pending_confirm && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: '#F59E0B22', color: '#F59E0B' }}>
                      Scorer reported a result
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-bold truncate" style={{ color: '#E63946' }}>{m.team_a_name}</span>
                      <input data-testid={`op-end-score-a-${m.slot_id}`} inputMode="numeric" value={r.a}
                        disabled={r.action !== 'save' || busy} onFocus={selectScore} onClick={selectScore}
                        onChange={e => setRow(m.slot_id, { a: e.target.value })} style={{ ...inputStyle, opacity: r.action === 'save' ? 1 : 0.4 }} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-bold truncate" style={{ color: '#3B82F6' }}>{m.team_b_name}</span>
                      <input data-testid={`op-end-score-b-${m.slot_id}`} inputMode="numeric" value={r.b}
                        disabled={r.action !== 'save' || busy} onFocus={selectScore} onClick={selectScore}
                        onChange={e => setRow(m.slot_id, { b: e.target.value })} style={{ ...inputStyle, opacity: r.action === 'save' ? 1 : 0.4 }} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2.5">
                  {[['save', 'Save result', '#22C55E'], ['discard', 'Discard', '#A3A3A3']].map(([key, label, color]) => (
                    <button key={key} data-testid={`op-end-${key}-${m.slot_id}`} disabled={busy}
                      onClick={() => setRow(m.slot_id, { action: key })}
                      className="flex-1 px-2 py-1.5 rounded-lg text-xs font-bold uppercase"
                      style={r.action === key
                        ? { background: `${color}22`, color, border: `1px solid ${color}66` }
                        : { background: 'transparent', color: '#525252', border: '1px solid #262626' }}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: error ? '#EF4444' : '#737373' }}>
                  {r.action === 'discard'
                    ? 'No result — nobody\'s record changes.'
                    : error || `Saves: ${winner === 'a' ? m.team_a_name : m.team_b_name} win ${Math.max(parseInt(r.a, 10), parseInt(r.b, 10))}–${Math.min(parseInt(r.a, 10), parseInt(r.b, 10))}`}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button data-testid="op-end-cancel" onClick={onCancel} disabled={busy}
            className="px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
            Cancel
          </button>
          <button data-testid="op-end-confirm" onClick={submit} disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-xs font-bold uppercase"
            style={{ background: '#EF4444', color: '#fff', opacity: canSubmit ? 1 : 0.5 }}>
            {busy ? 'Ending…' : 'End Session'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpenPlayPodiumModal({ podium, session, organization, onClose }) {
  const backdrop = useBackdropClose(onClose);
  // One podium per division (same ranking as the leaderboard); older payloads
  // without `divisions` fall back to the single top-level podium.
  const divs = podium.divisions?.length ? podium.divisions : [podium];
  const [divIdx, setDivIdx] = useState(() => Math.max(0, divs.findIndex(d => d.first)));
  const cur = divs[divIdx] || divs[0];
  const multi = divs.length > 1;
  // Players tied on the leaderboard share a place (co-champions) — they used
  // to be split into Champion and Runner-up by list order.
  const place = (key) => {
    const tier = cur.tiers?.[key] || (cur[key] ? [cur[key]] : []);
    if (tier.length <= 1) return tier[0] || null;
    return { ...tier[0], name: tier.map(p => p.name).join(' · '), avatar_url: null, tied: tier.length };
  };
  const first = place('first'); const second = place('second'); const third = place('third');
  const fair = isFairRule(cur);
  const cardEntry = (p) => (p ? { team_name: p.name, players: p.tied ? p.name.split(' · ') : [p.name], avatars: [p.avatar_url],
    stats: fair ? fairRecord(p) : `${p.wins} won · ${p.matches_played} played` } : null);
  const cardPayload = () => ({
    eventName: session?.name || 'Open Play',
    facilityName: organization?.name || '',
    date: session?.date,
    division: multi ? (cur.division_name || null) : null,
    podium: {
      // players as plain name strings + avatars + record line — the card draws all three
      first: cardEntry(first),
      second: cardEntry(second),
      third: cardEntry(third),
    },
    awards: cardAwards(cur.awards),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.92)' }} {...backdrop} data-testid="op-podium-modal">
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: 'radial-gradient(circle at 50% 0%, #1A1505, #0C0C0C 70%)', border: '1px solid #2A2410' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5" style={{ color: '#FFD54A' }} />
            <h2 className="text-base font-black uppercase tracking-widest" style={{ fontFamily: 'Oswald, sans-serif', color: '#FFD54A' }}>
              Session Champions
            </h2>
          </div>
          <button onClick={onClose} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <p className="text-xs text-center mb-5" style={{ color: '#737373' }}>{session?.name}</p>

        {multi && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-4" data-testid="op-podium-divisions">
            {divs.map((d, i) => (
              <button key={d.division_id || i} data-testid={`op-podium-div-${i}`}
                onClick={() => setDivIdx(i)} disabled={!d.first}
                title={d.first ? undefined : 'No completed games in this division'}
                className="px-3 py-1 rounded-full text-[11px] font-bold uppercase"
                style={!d.first ? { background: 'transparent', color: '#404040', border: '1px dashed #262626' } : i === divIdx
                  ? { background: '#FFD54A', color: '#0C0C0C' }
                  : { background: '#FFFFFF0A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                {d.division_name || `Division ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        {/* Podium */}
        <div className="flex items-end justify-center gap-2 sm:gap-4 mb-5">
          <PodiumStep place={2} player={second} fair={fair} />
          <PodiumStep place={1} player={first} fair={fair} />
          <PodiumStep place={3} player={third} fair={fair} />
        </div>
        {fair && <div className="mb-4 rounded-xl px-3 py-2.5" style={{ background: '#0B0B0B', border: '1px solid #2A2410' }}>
          <HowRanked minGames={cur.min_games} rule={cur.podium_rule} />
        </div>}
        {cur.awards?.length > 0 && <div className="mb-5"><OpenPlayAwards awards={cur.awards} /></div>}

        {/* Session Stats */}
        {podium.stats && (
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[
              { label: 'Matches', value: podium.stats.total_matches },
              { label: 'Players', value: podium.stats.total_players },
              { label: 'Avg Time', value: podium.stats.avg_match_duration ? `${podium.stats.avg_match_duration}m` : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl p-2.5 text-center" style={{ background: '#0F0F0F', border: '1px solid #2A2410' }}>
                <p className="text-lg font-black" style={{ fontFamily: 'Oswald, sans-serif', color: '#FFD54A' }}>{value}</p>
                <p className="text-[10px] uppercase" style={{ color: '#555' }}>{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Share / Download */}
        <div className="flex gap-2">
          <button data-testid="op-podium-share"
            onClick={() => shareChampionsCard(cardPayload())}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold uppercase"
            style={{ background: '#FFD54A1A', color: '#FFD54A', border: '1px solid #FFD54A40', fontFamily: 'Oswald, sans-serif' }}>
            <Share2 className="h-3.5 w-3.5" /> Share Card
          </button>
          <button data-testid="op-podium-download"
            onClick={() => downloadChampionsCard(cardPayload())}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold uppercase"
            style={{ background: '#FFFFFF0A', color: '#A3A3A3', border: '1px solid #2A2A2A', fontFamily: 'Oswald, sans-serif' }}>
            <Download className="h-3.5 w-3.5" /> Download
          </button>
        </div>
      </div>
    </div>
  );
}

function SkillSetModal({ onClose, onGenerate, isGenerating }) {
  const backdrop = useBackdropClose(onClose);
  const [order, setOrder] = useState('top_down');
  const [fairnessBoost, setFairnessBoost] = useState(false);
  const [split, setSplit] = useState('stacked');

  const SPLITS = [
    {
      value: 'stacked',
      label: 'Stacked',
      badge: 'SHOWCASE',
      badgeColor: '#F59E0B',
      desc: '1st + 2nd vs 3rd + 4th — strongest pair together.',
    },
    {
      value: 'balanced',
      label: 'Balanced',
      badge: 'FINALS MODE',
      badgeColor: '#22C55E',
      desc: '1st + 4th vs 2nd + 3rd — tightest possible game.',
    },
  ];

  const ORDERS = [
    {
      value: 'top_down',
      label: 'Top Down',
      badge: 'LEADERBOARD MODE',
      badgeColor: '#F59E0B',
      desc: 'Best players called first — "Top of the leaderboard, get ready!"',
      sub: 'Highest win rate gets the earliest match.',
    },
    {
      value: 'bottom_up',
      label: 'Bottom Up',
      badge: 'GRAND FINALE',
      badgeColor: '#8B5CF6',
      desc: 'Weaker players start — best players close the event.',
      sub: 'Builds excitement as skill level escalates each match.',
    },
    {
      value: 'fairness_skill',
      label: 'Fairness + Skill',
      badge: 'RECOMMENDED',
      badgeColor: '#22C55E',
      desc: 'Skill brackets sorted by who waited longest.',
      sub: 'Skilled players who sat out get called first over same-skill peers.',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)' }} {...backdrop} data-testid="skill-set-modal">
      <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#141414', border: '1px solid #333' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
              Generate Ranked Set
            </h3>
            <p className="text-xs mt-0.5" style={{ color: '#555' }}>
              Brackets by current standings — best vs best, going down
            </p>
          </div>
          <button onClick={onClose} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>

        {/* Call Order */}
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: '#555' }}>
          Call Order
        </p>
        <div className="space-y-2 mb-5">
          {ORDERS.map(opt => (
            <button key={opt.value} data-testid={`skill-order-${opt.value}`}
              onClick={() => setOrder(opt.value)}
              className="w-full text-left p-3 rounded-xl transition-all"
              style={{
                background: order === opt.value ? 'rgba(59,130,246,0.1)' : '#0F0F0F',
                border: `1px solid ${order === opt.value ? '#3B82F6' : '#2A2A2A'}`,
              }}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-sm font-bold" style={{ color: order === opt.value ? '#3B82F6' : '#fff' }}>
                  {opt.label}
                </span>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded"
                  style={{ background: `${opt.badgeColor}18`, color: opt.badgeColor }}>
                  {opt.badge}
                </span>
              </div>
              <p className="text-xs" style={{ color: '#A3A3A3' }}>{opt.desc}</p>
              <p className="text-[11px] mt-0.5" style={{ color: '#555' }}>{opt.sub}</p>
            </button>
          ))}
        </div>

        {/* Team Split */}
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: '#555' }}>
          Team Split
        </p>
        <div className="grid grid-cols-2 gap-2 mb-5">
          {SPLITS.map(opt => (
            <button key={opt.value} data-testid={`skill-split-${opt.value}`}
              onClick={() => setSplit(opt.value)}
              className="text-left p-3 rounded-xl transition-all"
              style={{
                background: split === opt.value ? 'rgba(59,130,246,0.1)' : '#0F0F0F',
                border: `1px solid ${split === opt.value ? '#3B82F6' : '#2A2A2A'}`,
              }}>
              <div className="flex items-center justify-between mb-0.5 gap-1">
                <span className="text-sm font-bold" style={{ color: split === opt.value ? '#3B82F6' : '#fff' }}>
                  {opt.label}
                </span>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0"
                  style={{ background: `${opt.badgeColor}18`, color: opt.badgeColor }}>
                  {opt.badge}
                </span>
              </div>
              <p className="text-[11px]" style={{ color: '#A3A3A3' }}>{opt.desc}</p>
            </button>
          ))}
        </div>

        {/* Fairness Boost Toggle */}
        <div className="flex items-start gap-3 p-3 rounded-xl mb-5"
          style={{ background: '#0F0F0F', border: `1px solid ${fairnessBoost ? '#22C55E44' : '#1A1A1A'}` }}>
          <button data-testid="fairness-boost-toggle"
            onClick={() => setFairnessBoost(v => !v)}
            className="mt-0.5 shrink-0 w-10 h-5 rounded-full relative transition-all"
            style={{ background: fairnessBoost ? '#22C55E' : '#2A2A2A' }}>
            <div className="absolute top-0.5 h-4 w-4 rounded-full transition-all"
              style={{ background: '#fff', left: fairnessBoost ? 'calc(100% - 18px)' : '2px' }} />
          </button>
          <div>
            <p className="text-sm font-bold" style={{ color: fairnessBoost ? '#22C55E' : '#fff' }}>
              Fairness Boost
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#555' }}>
              Players who sat out the previous set get priority over same-skill players in their bracket.
            </p>
          </div>
        </div>

        {/* Generate Button */}
        <button data-testid="skill-set-generate-btn"
          onClick={() => onGenerate({ skill_order: order, fairness_boost: fairnessBoost, skill_split: split })}
          disabled={isGenerating}
          className="w-full py-3 rounded-xl text-sm font-black uppercase"
          style={{
            background: '#3B82F6', color: '#fff',
            opacity: isGenerating ? 0.6 : 1,
            fontFamily: 'Oswald, sans-serif',
          }}>
          {isGenerating ? 'Generating…' : 'Generate Ranked Set →'}
        </button>
      </div>
    </div>
  );
}

function TeamMatchup({ taPlayers, tbPlayers }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        {taPlayers.map((p, i) => (
          <div key={p.id} className="flex items-center gap-2">
            {i > 0 && <span className="text-[10px] font-semibold" style={{ color: '#525252' }}>and</span>}
            {p.avatar_url ? (
              <img src={p.avatar_url} alt={p.name} className="h-5 w-5 rounded-full object-cover shrink-0"
                style={{ border: '1px solid #E6394655' }} />
            ) : (
              <div className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0"
                style={{ background: '#E6394622', color: '#E63946' }}>
                {p.name?.[0]?.toUpperCase()}
              </div>
            )}
            <span className="text-sm font-semibold" style={{ color: '#E63946' }}>{p.name}</span>
          </div>
        ))}
        {taPlayers.length === 0 && <span className="text-sm" style={{ color: '#E63946' }}>TBD</span>}
      </div>
      <div className="text-xs font-black" style={{ color: '#525252' }}>VS</div>
      <div className="flex items-center gap-2 flex-wrap">
        {tbPlayers.map((p, i) => (
          <div key={p.id} className="flex items-center gap-2">
            {i > 0 && <span className="text-[10px] font-semibold" style={{ color: '#525252' }}>and</span>}
            {p.avatar_url ? (
              <img src={p.avatar_url} alt={p.name} className="h-5 w-5 rounded-full object-cover shrink-0"
                style={{ border: '1px solid #3B82F655' }} />
            ) : (
              <div className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0"
                style={{ background: '#3B82F622', color: '#3B82F6' }}>
                {p.name?.[0]?.toUpperCase()}
              </div>
            )}
            <span className="text-sm font-semibold" style={{ color: '#3B82F6' }}>{p.name}</span>
          </div>
        ))}
        {tbPlayers.length === 0 && <span className="text-sm" style={{ color: '#3B82F6' }}>TBD</span>}
      </div>
    </div>
  );
}

// A TBA game in the list (owner 2026-10-05): the players saved in it plus its
// empty spots, filled automatically about one game before it plays.
function OpenGameRow({ game, n }) {
  const fixed = game.fixed || [];
  const half = Math.ceil((fixed.length + game.open_seats) / 2);
  const seats = [...fixed.map(p => ({ p })), ...Array.from({ length: game.open_seats }, () => ({}))];
  const side = (list) => list.map((x, k) => x.p ? (
    <span key={x.p.id} className="font-bold" style={{ color: 'inherit' }}>
      {x.p.name}{(game.late || []).includes(x.p.id) && <span className="ml-1 text-[9.5px] font-extrabold" style={{ color: '#FCD34D' }}>LATE</span>}
    </span>
  ) : (
    <span key={`tba-${k}`} className="inline-block text-[11px] font-extrabold px-2 py-px rounded-md"
      style={{ border: '1.5px dashed #F59E0B88', color: '#FCD34D', background: '#F59E0B0A', letterSpacing: '0.04em' }}>TBA</span>
  ));
  const sep = (arr) => arr.flatMap((el, k) => k ? [<span key={`s${k}`} style={{ color: '#666' }}>&amp;</span>, el] : [el]);
  return (
    <div data-testid={`up-next-open-${game.id}`} className="py-2 relative"
      style={{ background: 'linear-gradient(90deg, #F59E0B10, transparent 70%)' }}>
      <span className="absolute left-0 top-0 bottom-0" style={{ width: 4, background: '#F59E0B' }} />
      <div className="grid items-center gap-2" style={{ gridTemplateColumns: '2.25rem minmax(0,1fr)' }}>
        <span className="text-lg font-black tabular-nums text-right pl-2"
          style={{ color: n === 1 ? '#F59E0B' : '#444', fontFamily: 'Oswald, sans-serif' }}>#{n}</span>
        <div className="min-w-0 pr-3">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13.5px]">
            <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
              style={{ background: '#F59E0B', color: '#111', letterSpacing: '0.06em' }}>TBA GAME</span>
            <span className="inline-flex flex-wrap items-center gap-1.5" style={{ color: '#FF8A93' }}>{sep(side(seats.slice(0, half)))}</span>
            <span style={{ color: '#666' }}>vs</span>
            <span className="inline-flex flex-wrap items-center gap-1.5" style={{ color: '#8DBBFF' }}>{sep(side(seats.slice(half)))}</span>
          </div>
          <p className="text-[11.5px] mt-1 leading-snug" style={{ color: '#D6C08A' }}>
            {game.open_seats} spot{game.open_seats === 1 ? '' : 's'} filled automatically about one game before it plays:
            fewest games first, then even teams, then new partners. A late arrival takes a spot straight away.
          </p>
          {game.eta_minutes != null && (
            <p className="text-[11px] mt-0.5" style={{ color: '#737373' }}>
              {game.eta_minutes === 0 ? 'Next free court' : `About ${game.eta_minutes} min`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function UpNextQueue({ poolSlots, openGames = [], divisions = [], busyOn = {}, onCall, onScore, onSkip, onMove, actionLoading }) {
  if ((!poolSlots || poolSlots.length === 0) && openGames.length === 0) return null;
  const multiDiv = divisions.length > 1;
  const byDiv = {};
  (poolSlots || []).forEach(s => { byDiv[s.division_id] = byDiv[s.division_id] || []; byDiv[s.division_id].push(s); });
  const openByDiv = {};
  openGames.forEach(g => { openByDiv[g.division_id] = openByDiv[g.division_id] || []; openByDiv[g.division_id].push(g); });

  return (
    <div className="mt-5 space-y-4" data-testid="up-next-queue">
      {divisions.filter(d => (byDiv[d.id] || []).length > 0 || (openByDiv[d.id] || []).length > 0).map(d => {
        // The line as courts will take it: each open game sits at the end of its set.
        const slots = byDiv[d.id] || [];
        const opens = openByDiv[d.id] || [];
        const items = [];
        slots.forEach((sl, si) => {
          opens.filter(g => g.pool_index === si).forEach(g => items.push({ open: g }));
          items.push({ slot: sl, si });
        });
        opens.filter(g => g.pool_index >= slots.length).forEach(g => items.push({ open: g }));
        const head = items.slice(0, 4);
        return (
          <div key={d.id} className="op-card rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid #262626' }}>
            <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1" style={{ background: '#1C1C1C', borderBottom: '1px solid #262626' }}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-bold uppercase whitespace-nowrap" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                  Match queue{multiDiv ? ` — ${d.name}` : ''}
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase whitespace-nowrap"
                  style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B30' }}>
                  Shared queue
                </span>
              </div>
              <span className="text-[10px]" style={{ color: '#555' }}>paired · waiting for a free court</span>
            </div>
            <div className="divide-y" style={{ borderColor: '#1A1A1A' }}>
              {head.map((item, i) => item.open ? <OpenGameRow key={item.open.id} game={item.open} n={i + 1} /> : (({ slot, si }) => (
                <div key={slot.id} data-testid={`up-next-${i + 1}`} className="py-1">
                  <div className="grid items-center gap-2"
                    style={{ gridTemplateColumns: '2.25rem minmax(0,1fr)' }}>
                    <span className="text-lg font-black tabular-nums text-right pl-2"
                      style={{ color: i === 0 ? '#F59E0B' : '#444', fontFamily: 'Oswald, sans-serif' }}>
                      #{i + 1}
                    </span>
                    <MatchupCard variant="queued" nextUp={i === 0}
                      teamA={slot.team_a_players || []} teamB={slot.team_b_players || []}
                      etaMinutes={slot.eta_minutes} />
                  </div>
                  {i === 0 && (() => {
                    // The queue never skips this match while one of its players is in a game:
                    // say so, or an empty court looks like a bug (owner 2026-10-01).
                    const busy = [...(slot.team_a_players || []), ...(slot.team_b_players || [])].filter(p => busyOn[p.id]);
                    if (!busy.length) return null;
                    return (
                      <p data-testid={`up-next-waiting-${slot.id}`} className="text-[11.5px] mt-1.5 ml-11 mr-3 px-2.5 py-1.5 rounded-lg leading-snug"
                        style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B44' }}>
                        <b style={{ color: '#FCD34D' }}>Waiting for {busy.map(p => p.name).join(' & ')}</b> — still playing on {[...new Set(busy.map(p => busyOn[p.id]))].join(' & ')}.
                        {' '}This match starts when {busy.length > 1 ? 'they finish' : 'that game ends'}; a free court waits until then.
                      </p>
                    );
                  })()}
                  {(slot.last_game_for || []).length > 0 && (
                    <p data-testid={`up-next-last-${slot.id}`} className="text-[11px] mt-1 pl-11 flex items-center gap-1.5" style={{ color: '#A3A3A3' }}>
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
                        style={{ color: '#FCD34D', border: '1px solid #F59E0B66', letterSpacing: '0.06em' }}>
                        LAST GAME · {[...(slot.team_a_players || []), ...(slot.team_b_players || [])]
                          .filter(p => slot.last_game_for.includes(p.id)).map(p => p.name).join(' & ')}
                      </span>
                      Done after this game
                    </p>
                  )}
                  {i > 0 && head[i - 1].slot && (() => {
                    const prev = head[i - 1].slot;
                    const before = new Set([...(prev.team_a || []), ...(prev.team_b || [])]);
                    const twice = [...(slot.team_a_players || []), ...(slot.team_b_players || [])].filter(p => before.has(p.id));
                    if (!twice.length) return null;
                    return (
                      <p data-testid={`up-next-twice-${slot.id}`} className="text-[11px] mt-1 pl-11" style={{ color: '#FCD34D' }}>
                        ⚠ {twice.map(p => p.name).join(', ')} also {twice.length === 1 ? 'plays' : 'play'} in #{i} — two games in a row
                      </p>
                    );
                  })()}
                  {slot.custom_match && (
                    <p data-testid={`up-next-custom-${slot.id}`} className="text-[11px] mt-1 pl-11 flex items-center gap-1.5" style={{ color: '#A3A3A3' }}>
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
                        style={{ background: '#F59E0B', color: '#111', letterSpacing: '0.06em' }}>CUSTOM MATCH</span>
                      Made by the coordinator
                    </p>
                  )}
                  {slot.open_game && (
                    <p data-testid={`up-next-openfilled-${slot.id}`} className="text-[11px] mt-1 pl-11 flex flex-wrap items-center gap-1.5" style={{ color: '#A3A3A3' }}>
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
                        style={{ color: '#4ADE80', border: '1px solid #22C55E66', letterSpacing: '0.06em' }}>TBA GAME · FILLED</span>
                      {(() => {
                        const all = [...(slot.team_a_players || []), ...(slot.team_b_players || [])];
                        const name = id => (all.find(p => p.id === id) || {}).name;
                        const late = (slot.open_game_late || []).map(name).filter(Boolean);
                        const filled = (slot.open_game_filled || []).map(name).filter(Boolean);
                        return [late.length ? `${late.join(' & ')} arrived late` : null,
                          filled.length ? `${filled.join(', ')} picked automatically` : null].filter(Boolean).join(' · ');
                      })()}
                    </p>
                  )}
                  {slot.late_insert && (
                    <p data-testid={`up-next-late-${slot.id}`} className="text-[11px] mt-1 pl-11 flex items-center gap-1.5" style={{ color: '#A3A3A3' }}>
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
                        style={{ background: '#F59E0B', color: '#111', letterSpacing: '0.06em' }}>LATE ARRIVAL</span>
                      Pushed to the front for players who checked in late
                    </p>
                  )}
                  {slot.requeued_from_court && ((slot.score_a || 0) + (slot.score_b || 0)) > 0 && (
                    <p data-testid={`up-next-resumes-${slot.id}`} className="text-[11px] mt-1 pl-11" style={{ color: '#F59E0B' }}>
                      Moved off a paused court — resumes at {slot.score_a || 0}–{slot.score_b || 0}
                    </p>
                  )}
                  {/* Per-match module (own row so it never crowds the matchup):
                      view QR / open scorer + call to court (floats if all busy) */}
                  <div className="flex items-center justify-end gap-1.5 mt-1.5 pr-3 pl-9 flex-wrap">
                    {onMove && (
                      <span className="inline-flex rounded overflow-hidden mr-auto" style={{ border: '1px solid #2A2A2A' }}>
                        {[['front', ArrowUpToLine, 'Move to the front', si === 0],
                          ['up', ChevronUp, 'Move up', si === 0],
                          ['down', ChevronDown, 'Move down', si === slots.length - 1]].map(([to, Icon, label, edge], k) => (
                          <button key={to} type="button" data-testid={`move-${to}-${slot.id}`} title={label} aria-label={label}
                            disabled={edge || actionLoading === `move-${slot.id}`} onClick={() => onMove(slot, to)}
                            className="w-8 h-7 inline-flex items-center justify-center"
                            style={{ background: '#161616', color: edge ? '#3A3A3A' : '#BDBDBD', borderLeft: k ? '1px solid #2A2A2A' : 0 }}>
                            <Icon className="h-3.5 w-3.5" />
                          </button>
                        ))}
                      </span>
                    )}
                    {onScore && slot.qr_token && (
                      <button data-testid={`queue-qr-${slot.id}`}
                        onClick={() => onScore(slot)}
                        title="Show QR / open scorer link"
                        className="text-xs px-2.5 py-1 rounded font-bold inline-flex items-center gap-1"
                        style={{ background: '#161616', color: '#E5E5E5', border: '1px solid #2A2A2A' }}>
                        <QrCode className="h-3 w-3" /> QR / Score
                      </button>
                    )}
                    {onSkip && (
                      <button data-testid={`skip-slot-${slot.id}`}
                        disabled={actionLoading === `void-${slot.id}`}
                        onClick={() => onSkip(slot)}
                        title="Remove this match from the queue (nothing is recorded)"
                        className="text-xs px-2.5 py-1 rounded font-bold"
                        style={{ background: '#1A1A1A', color: '#737373', border: '1px solid #2A2A2A' }}>
                        {slot.late_insert || slot.custom_match ? 'Remove' : 'Skip'}
                      </button>
                    )}
                    {onCall && (
                      <button data-testid={`call-slot-${slot.id}`}
                        disabled={actionLoading === `call-${slot.id}`}
                        onClick={() => onCall(slot.id)}
                        title="Call to court now — floats (TBA) if every court is busy, then drops onto the next one that opens"
                        className="text-xs px-2.5 py-1 rounded font-bold inline-flex items-center gap-1"
                        style={{ background: '#161616', color: '#fff', border: '1px solid #3A3A3A' }}>
                        {actionLoading === `call-${slot.id}` ? '…' : 'Call to court'}
                      </button>
                    )}
                  </div>
                </div>
              ))(item))}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function SetScheduleTable({ poolSlots, openGames = [], divisions = [], onMove = null, actionLoading }) {
  if ((!poolSlots || poolSlots.length === 0) && openGames.length === 0) return null;
  // Place in the division's queue (#1 = next) — the raw slot_position is internal.
  const place = {};
  const seen = {};
  poolSlots.forEach(sl => { const k = sl.division_id || 'x'; seen[k] = (seen[k] || 0) + 1; place[sl.id] = seen[k]; });

  const divNames = {};
  divisions.forEach(d => { divNames[d.id] = d.name; });
  const multiDiv = divisions.length > 1;

  // Group by (division, set_number)
  const groups = {};
  poolSlots.forEach(slot => {
    const key = `${slot.division_id || 'x'}::${slot.set_number || 1}`;
    if (!groups[key]) groups[key] = { slots: [], setType: slot.set_type || 'open_play', setNum: slot.set_number || 1, divId: slot.division_id };
    groups[key].slots.push(slot);
  });
  // Each set's TBA game is listed at the end of its set (2026-10-05).
  openGames.forEach(g => {
    const key = `${g.division_id || 'x'}::${g.set_number || 1}`;
    if (!groups[key]) groups[key] = { slots: [], setType: 'open_play', setNum: g.set_number || 1, divId: g.division_id };
    (groups[key].opens = groups[key].opens || []).push(g);
  });

  return (
    <div className="mt-6" data-testid="set-schedule-table">
      {Object.values(groups)
        .sort((a, b) => (divNames[a.divId] || '').localeCompare(divNames[b.divId] || '') || a.setNum - b.setNum)
        .map(group => {
        const setNum = group.setNum;
        const isSkill = group.setType === 'skill_based';
        const stLabel = isSkill ? 'RANKED SET' : 'ROTATION';
        const stColor = isSkill ? '#3B82F6' : '#22C55E';
        const divLabel = multiDiv && divNames[group.divId] ? `${divNames[group.divId]} — ` : '';
        return (
          <div key={`${group.divId}-${setNum}`} className="mb-5">
            {/* Set header */}
            <div className="flex items-center gap-3 mb-2">
              <div style={{ flex: 1, height: 1, background: '#262626' }} />
              <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full shrink-0"
                style={{ color: stColor, background: `${stColor}14`, border: `1px solid ${stColor}30`, fontFamily: 'Oswald, sans-serif' }}>
                {divLabel}Set {setNum} — {stLabel}
              </span>
              <div style={{ flex: 1, height: 1, background: '#262626' }} />
            </div>
            {/* Match rows — shared MatchupCard, compact owner density (checklist 1.9) */}
            <div className="op-card rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid #262626' }}
              data-testid={`set-table-${setNum}`}>
              {group.slots.map((slot, idx) => (
                <div key={slot.id} data-testid={`set-slot-${slot.id}`} className="grid items-center"
                  style={{ gridTemplateColumns: '2rem minmax(0,1fr)',
                    borderTop: idx > 0 ? '1px solid #1C1C1C' : 'none' }}>
                  <span className="pl-3 text-xs font-black tabular-nums text-right"
                    style={{ color: '#444', fontFamily: 'Oswald, sans-serif' }}>
                    #{place[slot.id]}
                  </span>
                  <div className="min-w-0">
                    <MatchupCard variant="compact"
                      teamA={slot.team_a_players || []} teamB={slot.team_b_players || []} />
                    {(slot.custom_match || (slot.last_game_for || []).length > 0 || (onMove && place[slot.id] > 1)) && (
                      <div className="flex items-center gap-1.5 px-3 pb-1.5 -mt-0.5">
                        {slot.custom_match && (
                          <span data-testid={`set-custom-${slot.id}`} className="text-[9.5px] font-extrabold px-1.5 py-0.5 rounded"
                            style={{ background: '#F59E0B', color: '#111', letterSpacing: '0.06em' }}>CUSTOM</span>
                        )}
                        {(slot.last_game_for || []).length > 0 && (
                          <span className="text-[9.5px] font-extrabold px-1.5 py-0.5 rounded"
                            style={{ color: '#FCD34D', border: '1px solid #F59E0B66', letterSpacing: '0.06em' }}>LAST GAME</span>
                        )}
                        {onMove && place[slot.id] > 1 && (
                          <button type="button" data-testid={`set-front-${slot.id}`} onClick={() => onMove(slot, 'front')}
                            disabled={actionLoading === `move-${slot.id}`}
                            className="ml-auto text-[10.5px] font-bold px-2 py-0.5 rounded inline-flex items-center gap-1"
                            style={{ background: '#161616', color: '#BDBDBD', border: '1px solid #2A2A2A' }}
                            title="Move this match to the front of the queue">
                            <ArrowUpToLine className="h-3 w-3" /> Front
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(group.opens || []).map(g => (
                <div key={g.id} data-testid={`set-open-${g.id}`} className="flex flex-wrap items-center gap-1.5 px-3 py-2 text-[12.5px]"
                  style={{ borderTop: group.slots.length ? '1px solid #1C1C1C' : 'none', background: 'linear-gradient(90deg, #F59E0B0D, transparent 70%)' }}>
                  <span className="text-[9.5px] font-extrabold px-1.5 py-0.5 rounded"
                    style={{ background: '#F59E0B', color: '#111', letterSpacing: '0.06em' }}>TBA GAME</span>
                  <span className="font-bold" style={{ color: '#E5E5E5' }}>{(g.fixed || []).map(p => p.name).join(' & ')}</span>
                  <span style={{ color: '#8A8A8A' }}>+ {g.open_seats} TBA — picked about one game before it plays</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-right mt-1 pr-1" style={{ color: '#444' }}>
              {group.slots.length} match{group.slots.length !== 1 ? 'es' : ''}
              {(group.opens || []).length ? ` + ${group.opens.length} TBA game` : ''}
            </p>
          </div>
        );
      })}
    </div>
  );
}


function DivisionPickerModal({ divisions, setType, onClose, onPick }) {
  const backdrop = useBackdropClose(onClose);
  const isSkill = setType === 'skill_based';
  const color = isSkill ? '#3B82F6' : '#22C55E';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }} {...backdrop} data-testid="division-picker-modal">
      <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#141414', border: '1px solid #262626' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-black uppercase tracking-widest" style={{ fontFamily: 'Oswald, sans-serif', color }}>
            {isSkill ? 'Ranked Set' : 'Rotation Set'} — Pick Division
          </h3>
          <button onClick={onClose} style={{ color: '#737373' }} data-testid="division-picker-close"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs mb-4" style={{ color: '#737373' }}>
          The set is generated only for the selected division and queued to its courts.
        </p>
        <div className="space-y-2">
          {divisions.map(d => (
            <button key={d.id} data-testid={`division-pick-${d.id}`} onClick={() => onPick(d.id)}
              className="w-full text-left px-4 py-3 rounded-xl text-sm font-bold flex items-center justify-between gap-3"
              style={{ background: '#0A0A0A', border: '1px solid #262626', color: '#fff' }}>
              <span>{d.name}</span>
              <span className="text-[10px] uppercase" style={{ color: '#555' }}>
                {(d.court_ids || []).length} court{(d.court_ids || []).length !== 1 ? 's' : ''}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function AddCourtModal({ sessionId, divId, divName, onClose, onAdded }) {
  const backdrop = useBackdropClose(onClose);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    sportsApi.get(`/sports/open-play/${sessionId}/available-courts`)
      .then(r => setCourts(r.data || []))
      .catch(() => setCourts([]))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const addCourt = async (courtId) => {
    setAdding(courtId);
    try {
      const res = await sportsApi.put(`/sports/open-play/${sessionId}/divisions/${divId}/add-court`, { court_id: courtId });
      toast.success(`Court added — ${res.data.slots_generated} matchup(s) generated`);
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add court');
      setAdding(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)' }} {...backdrop} data-testid="add-court-modal">
      <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#141414', border: '1px solid #333' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
              Open New Court
            </h3>
            {divName && <p className="text-xs mt-0.5" style={{ color: '#737373' }}>Division: {divName}</p>}
          </div>
          <button onClick={onClose} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        {loading ? (
          <p className="text-xs text-center py-6" style={{ color: '#555' }}>Loading courts…</p>
        ) : courts.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm font-bold" style={{ color: '#737373' }}>No available courts</p>
            <p className="text-xs mt-1" style={{ color: '#555' }}>All facility courts are already assigned. Add more courts in My Courts.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs mb-3" style={{ color: '#737373' }}>Select a court to activate for this division:</p>
            {courts.map(c => (
              <button key={c.id} data-testid={`add-court-btn-${c.id}`}
                onClick={() => addCourt(c.id)} disabled={!!adding}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all"
                style={{ background: '#0A0A0A', border: `1px solid ${adding === c.id ? '#22C55E' : '#2A2A2A'}`, color: '#fff' }}>
                <span className="font-semibold">{c.name}</span>
                <span className="text-xs" style={{ color: '#737373' }}>
                  {adding === c.id ? 'Adding…' : c.sport || 'Multi-sport'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ScorerLaunchModal({ slot, scorerUrl, onClose }) {
  const backdrop = useBackdropClose(onClose);
  const taNames = (slot.team_a_players || []).map(p => p.name).join(' & ') || 'Team A';
  const tbNames = (slot.team_b_players || []).map(p => p.name).join(' & ') || 'Team B';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)' }} {...backdrop} data-testid="scorer-launch-modal">
      <div className="w-full max-w-xs rounded-2xl p-5" style={{ background: '#141414', border: '1px solid #333' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-black uppercase tracking-wide"
            style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
            Open Scorer
          </h3>
          <button onClick={onClose} data-testid="scorer-modal-close" style={{ color: '#737373' }}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs mb-4" style={{ color: '#737373' }}>
          <span style={{ color: '#E63946' }}>{taNames}</span>
          <span style={{ color: '#525252' }}> vs </span>
          <span style={{ color: '#3B82F6' }}>{tbNames}</span>
        </p>

        {/* QR Code */}
        <div className="flex flex-col items-center rounded-xl p-4 mb-4"
          style={{ background: '#fff' }}>
          <QRCodeSVG value={scorerUrl} size={180} fgColor="#0A0A0A" bgColor="#fff" level="M" />
          <p className="text-[10px] mt-2 text-center font-medium" style={{ color: '#555' }}>
            Scan with your phone camera to score
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-2 mb-4">
          <div style={{ flex: 1, height: 1, background: '#2A2A2A' }} />
          <span className="text-xs" style={{ color: '#555' }}>or open directly</span>
          <div style={{ flex: 1, height: 1, background: '#2A2A2A' }} />
        </div>

        {/* Direct links */}
        <a data-testid="scorer-modal-open-link"
          href={scorerUrl} target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-black uppercase mb-2"
          style={{ background: '#8B5CF6', color: '#fff', fontFamily: 'Oswald, sans-serif', textDecoration: 'none' }}>
          <Smartphone className="h-4 w-4" /> Open Scorer in New Tab
        </a>
        <a data-testid="scorer-modal-watch-link"
          href={`${window.location.origin}/sports/open-play/watch/${slot.qr_token}`} target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-bold uppercase"
          style={{ background: '#E6394618', color: '#E63946', border: '1px solid #E6394644', textDecoration: 'none' }}>
          <Eye className="h-4 w-4" /> Watch Live (Spectator)
        </a>
      </div>
    </div>
  );
}

// Above every dialog it can be asked from (Replace, Make a match, Last game,
// Edit name, Give spot, late arrival sit at z-60; menus z-70; takeover z-80).
function PinPromptModal({ onSubmit, onCancel, error }) {
  const backdrop = useBackdropClose(onCancel);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (pin.length < 4) return;
    setSubmitting(true);
    await onSubmit(pin);
    setSubmitting(false);
  };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }} {...backdrop} data-testid="op-pin-modal">
      <div className="w-full max-w-xs rounded-2xl p-5" style={{ background: '#141414', border: '1px solid #333' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>🔒 Session PIN</h3>
          <button onClick={onCancel} data-testid="op-pin-close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: '#737373' }}>Enter the 4–6 digit PIN to unlock owner actions for this session on this device.</p>
        <input autoFocus data-testid="op-pin-input" type="password" inputMode="numeric" maxLength={6}
          value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="••••"
          style={{ width: '100%', textAlign: 'center', letterSpacing: 8, fontSize: 22, padding: '10px 12px',
            background: '#0A0A0A', border: `1px solid ${error ? '#EF4444' : '#2A2A2A'}`, borderRadius: 8,
            color: '#fff', outline: 'none' }} />
        {error && <p className="text-xs mt-2" style={{ color: '#EF4444' }}>{error}</p>}
        <button data-testid="op-pin-submit" onClick={submit} disabled={pin.length < 4 || submitting}
          className="mt-4 w-full py-2.5 rounded-lg text-sm font-black uppercase"
          style={{ background: '#E63946', color: '#fff', opacity: (pin.length < 4 || submitting) ? 0.5 : 1, fontFamily: 'Oswald, sans-serif' }}>
          {submitting ? 'Verifying…' : 'Unlock'}
        </button>
      </div>
    </div>
  );
}

function AddPlayerModal({ session, sessionId, players = [], onClose, onAdded }) {
  const backdrop = useBackdropClose(onClose);
  const [form, setForm] = useState({ name: '', phone: '', email: '', gender: '', division_id: session.divisions?.length === 1 ? session.divisions[0].id : '', payment_status: 'at_venue' });
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState(null); // {avatar_key, avatar_thumb_key, preview}
  const [uploading, setUploading] = useState(false);
  // Starting level for every session: the 1-10 bar or a typed DUPR (plan 7.2)
  const [skill, setSkill] = useState({ self_skill: 5, dupr: '' });
  const { organization } = useSportsAuth();
  const fee = parseFloat(session.entry_fee || 0);
  const inp = { width: '100%', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 14, outline: 'none' };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Photo must be under 8MB'); return; }
    const slug = organization?.slug;
    if (!slug) { toast.error('Missing organization'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await sportsApi.post(`/sports/public/${slug}/open-play/${sessionId}/upload-avatar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const preview = URL.createObjectURL(file);
      setAvatar({ ...res.data, preview });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error('Name and phone are required'); return; }
    if ((session.divisions || []).length > 1 && !form.division_id) { toast.error('Pick a division'); return; }
    setSaving(true);
    try {
      const added = await sportsApi.post(`/sports/open-play/${sessionId}/players`, {
        ...form,
        payment_status: fee > 0 ? form.payment_status : undefined,
        avatar_key: avatar?.avatar_key,
        avatar_thumb_key: avatar?.avatar_thumb_key,
        self_skill: skill.self_skill,
        dupr: skill.dupr || undefined,
      });
      toast.success(added.data?.status === 'active' ? 'Player added and checked in — they join the rotation' : 'Player added');
      if (added.data?.capacity_warning) toast.warning(added.data.capacity_warning, { duration: 8000 });
      onAdded();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to add player'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}
      {...backdrop} data-testid="op-add-player-modal">
      <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#141414', border: '1px solid #333' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>Add Walk-in Player</h3>
          <button onClick={onClose} data-testid="op-add-close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          {/* Photo uploader */}
          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer"
            style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}
            data-testid="op-add-photo-label">
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: avatar?.preview ? `url(${avatar.preview}) center/cover` : '#1A1A1A',
              border: '2px solid #2A2A2A', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {!avatar && <UserPlus className="h-5 w-5" style={{ color: '#525252' }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="text-xs font-bold uppercase" style={{ color: '#A3A3A3' }}>
                {uploading ? 'Uploading…' : avatar ? 'Photo attached' : 'Add player photo'}
              </p>
              <p className="text-[11px]" style={{ color: '#555' }}>
                {avatar ? 'Tap to replace · optional' : 'JPG/PNG · up to 8MB · optional'}
              </p>
            </div>
            <input type="file" accept="image/*" onChange={handlePhoto} data-testid="op-add-photo" style={{ display: 'none' }} />
          </label>
          <input data-testid="op-add-name" style={inp} placeholder="Name *" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} />
          <input data-testid="op-add-phone" style={inp} placeholder="Phone *" value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })} />
          <input data-testid="op-add-email" style={inp} placeholder="Email (optional)" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })} />
          {/* Gender — optional; powers early-round M/F balancing */}
          <div>
            <p className="text-[11px] mb-1" style={{ color: '#737373' }}>Gender <span style={{ color: '#525252' }}>(optional — balances early games)</span></p>
            <div className="flex gap-1.5">
              {[['M', '♂ Male', '#3B82F6'], ['F', '♀ Female', '#EC4899'], ['', 'Skip', '#737373']].map(([val, label, color]) => (
                <button key={label} type="button" data-testid={`op-add-gender-${val || 'skip'}`}
                  onClick={() => setForm({ ...form, gender: val })}
                  className="flex-1 py-2 rounded-lg text-xs font-bold uppercase"
                  style={form.gender === val
                    ? { background: `${color}22`, color, border: `1px solid ${color}66` }
                    : { background: '#0F0F0F', color: '#737373', border: '1px solid #2A2A2A' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {(session.divisions || []).length > 1 && (
            <select data-testid="op-add-division" style={inp} value={form.division_id}
              onChange={e => setForm({ ...form, division_id: e.target.value })}>
              <option value="">Pick division *</option>
              {session.divisions.map(d => <option key={d.id} value={d.id}>{d.name}{hasRange(d) ? ` (level ${d.min_level}–${d.max_level})` : ''}</option>)}
            </select>
          )}
          {(() => {
            const div = (session.divisions || []).find(d => d.id === form.division_id);
            const fit = divisionFit(div, skill.self_skill);
            if (!fit || fit === 'ok') return null;
            const better = fittingDivision(session.divisions, skill.self_skill);
            return (
              <p data-testid="op-add-division-advice" className="text-[11px] px-2 py-1.5 rounded"
                style={{ background: 'rgba(245,158,11,0.08)', color: '#F5C26B', border: '1px solid rgba(245,158,11,0.3)' }}>
                Level {Math.round(skill.self_skill)} is {fit} {div.name} ({div.min_level}–{div.max_level})
                {better ? ` — ${better.name} fits better.` : '.'} You can still add them here.
              </p>
            );
          })()}
          {fee > 0 && (
            <select data-testid="op-add-payment" style={inp} value={form.payment_status}
              onChange={e => setForm({ ...form, payment_status: e.target.value })}>
              <option value="at_venue">Pay at venue (₱{fee})</option>
              <option value="paid">Already paid</option>
              <option value="free">Waived / free</option>
            </select>
          )}
          <div data-testid="op-add-skill-block">
            <SkillInput value={skill} onChange={setSkill} sliderId="op-add-skill-slider" testid="op-add-skill" />
          </div>
        </div>
        {(() => {
          // Over 16 per court (owner 2026-09-30): allowed, but say what it costs.
          const divs = session.divisions || [];
          const div = divs.find(d => d.id === form.division_id) || (divs.length === 1 ? divs[0] : null);
          if (!div || !['active', 'registration_closed', 'registration_open'].includes(session.status)) return null;
          const courts = (div.court_ids || []).filter(Boolean).length || 1;
          const here = players.filter(p => p.division_id === div.id && ['active', 'playing', 'paused'].includes(p.status)).length;
          if (here + 1 <= 16 * courts) return null;
          return (
            <p data-testid="op-add-capacity-warning" className="text-xs mt-3 px-3 py-2 rounded-lg"
              style={{ background: '#F59E0B12', border: '1px solid #F59E0B40', color: '#FCD34D' }}>
              This makes {here + 1} players on {courts} court{courts === 1 ? '' : 's'} — above the {16 * courts} we recommend.
              Everyone will get fewer games. You can still add them.
            </p>
          );
        })()}
        <button data-testid="op-add-submit" onClick={submit} disabled={saving || uploading}
          className="mt-4 w-full py-2.5 rounded-lg text-sm font-black uppercase"
          style={{ background: '#E63946', color: '#fff', opacity: saving ? 0.6 : 1, fontFamily: 'Oswald, sans-serif' }}>
          {saving ? 'Adding…' : 'Add Player'}
        </button>
      </div>
    </div>
  );
}

function ProofModal({ player, onClose, onCollect }) {
  const backdrop = useBackdropClose(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}
      {...backdrop} data-testid="op-proof-modal">
      <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#141414', border: '1px solid #333' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{player.name}</h3>
            <p className="text-xs" style={{ color: '#737373' }}>GCash payment proof · ₱{player.amount_due || 0}</p>
          </div>
          <button onClick={onClose} data-testid="op-proof-close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <img src={player.proof_url} alt="Payment proof"
          style={{ width: '100%', maxHeight: 380, objectFit: 'contain', borderRadius: 10, background: '#0A0A0A' }} />
        {player.payment_status !== 'paid' && (
          <button data-testid="op-proof-collect" onClick={onCollect}
            className="mt-3 w-full py-2.5 rounded-lg text-sm font-black uppercase"
            style={{ background: '#22C55E', color: '#000', fontFamily: 'Oswald, sans-serif' }}>
            ✓ Review &amp; Record This Payment
          </button>
        )}
      </div>
    </div>
  );
}

// The owner console is the app's own space, so it glows in the AgriBooks red
// (owner 2026-09-27); venue colours stay on the public pages.
const CONSOLE_ACCENT = '#E63946';
const CONSOLE_GLOW = 'rgba(230,57,70,0.28)';

// Glow system (owner 2026-09-27, modelled on the public landing page):
// resting glow on the main boxes in the venue's colour; a breathing glow +
// pulsing dot where a game is live; amber pulse where something waits on the
// owner; the one main action glows amber; paused courts dim; reduced motion
// turns every animation off. --vglow / --vacc come from the venue theme.
const CONSOLE_GLOW_CSS = `
/* Boxes like the public site's session cards (owner 2026-09-27 reference):
   a brand-tinted dark surface and border instead of neutral grey, and a 5px
   status rail on the left edge of boxes that carry a state. */
.op-console .op-card, .op-console .op-live, .op-console .op-dim, .op-console [data-testid="tba-lane"] {
  background: linear-gradient(180deg, #1A0D10 0%, #120A0C 100%) !important;
  border: 1px solid #3A1A20 !important; border-radius: 14px !important; position: relative; }
.op-console .op-card { box-shadow: inset 0 1px 0 rgba(255,255,255,0.04), 0 18px 46px -26px var(--vglow); }
.op-console [data-testid^="court-card-"]::before, .op-console [data-testid="tba-lane"]::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 5px; z-index: 2;
  background: linear-gradient(180deg, var(--rail, #5A3A3F), color-mix(in srgb, var(--rail, #5A3A3F) 50%, transparent)); }
.op-console .op-live::before { --rail: var(--vacc); }
.op-console .op-dim::before { --rail: #6B5A5C; }
.op-console [data-testid="tba-lane"]::before { --rail: #F59E0B; }
/* box headers: no grey slab — a tinted divider only */
.op-console [data-testid^="court-card-"] > div:first-child,
.op-console .op-card > div:first-child[style*="border-bottom"] {
  background: transparent !important; border-bottom-color: #2E161B !important; }
/* status strip in the same family */
.op-console [data-testid="op-status-strip"] > span, .op-console [data-testid="op-status-strip"] > button {
  background: #0E0708 !important; border-color: #2E161B !important; }
.op-console .op-live { border-color: color-mix(in srgb, var(--vacc) 42%, transparent) !important;
  animation: opBreathe 2.8s ease-in-out infinite; }
@keyframes opBreathe {
  0%, 100% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px color-mix(in srgb, var(--vacc) 14%, transparent), 0 12px 34px -16px var(--vglow); }
  50% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px color-mix(in srgb, var(--vacc) 30%, transparent), 0 16px 50px -10px var(--vglow); } }
.op-console .op-live-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  background: var(--vacc); box-shadow: 0 0 10px var(--vacc); animation: opDot 1.6s ease-in-out infinite; }
@keyframes opDot { 50% { opacity: 0.3; } }
/* late-arrival pill: one glow pulse when another late player checks in */
.op-console .op-late-pulse { animation: opLatePulse 0.9s ease-out 2; }
@keyframes opLatePulse {
  0% { box-shadow: 0 0 0 0 rgba(245,158,11,0.75); }
  100% { box-shadow: 0 0 0 12px rgba(245,158,11,0); } }
@media (prefers-reduced-motion: reduce) { .op-console .op-late-pulse { animation: none; } }
.op-console .op-dim { opacity: 0.62; }
.op-console .op-attn { animation: opAttn 2.2s ease-in-out infinite; }
@keyframes opAttn {
  0%, 100% { box-shadow: 0 0 0 1px rgba(245,158,11,0.18), 0 0 16px -8px rgba(245,158,11,0.35); }
  50% { box-shadow: 0 0 0 1px rgba(245,158,11,0.45), 0 0 24px -4px rgba(245,158,11,0.55); } }
.op-console .op-amber-glow { box-shadow: 0 6px 22px -6px rgba(245,158,11,0.75); }
.op-console .op-amber-soft { box-shadow: 0 0 20px -8px rgba(245,158,11,0.55); }
.op-console .op-score { box-shadow: 0 0 22px -8px var(--vglow), inset 0 0 14px -10px var(--vacc); }
.op-console .op-score-flash { animation: opFlash 700ms cubic-bezier(0.23,1,0.32,1); }
@keyframes opFlash {
  0% { box-shadow: 0 0 0 2px var(--vacc), 0 0 34px var(--vglow); }
  100% { box-shadow: 0 0 22px -8px var(--vglow), inset 0 0 14px -10px var(--vacc); } }
@media (prefers-reduced-motion: reduce) {
  .op-console .op-live, .op-console .op-live-dot, .op-console .op-attn, .op-console .op-score-flash { animation: none !important; } }
`;

// Console colour rules (owner 2026-09-27): amber = the one main action;
// red = live and danger only; green = good / done; red & blue = Team A / B;
// everything else calm neutral.
const C = { amber: '#F59E0B', danger: '#EF4444', ok: '#22C55E', line: '#2A2A2A' };

function HeaderBtn({ icon: Icon, label, onClick, testid, disabled, variant = 'neutral', title }) {
  const look = {
    primary: { background: C.amber, color: '#111', border: `1px solid ${C.amber}` },
    danger: { background: 'transparent', color: '#FF6B76', border: '1px solid #EF444466' },
    neutral: { background: '#161616', color: '#E5E5E5', border: `1px solid ${C.line}` },
  }[variant];
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} title={title} type="button"
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-bold active:scale-[0.97] ${variant === 'primary' ? 'op-amber-glow' : ''}`}
      style={{ ...look, opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'transform 160ms cubic-bezier(0.23,1,0.32,1)' }}>
      {Icon && <Icon className="h-4 w-4" />} {label}
    </button>
  );
}

// One "More" menu for the actions that aren't the stage's main job.
// groups: [{ title, items: [{ icon, label, hint?, onClick, testid, disabled } | false] }]
function MoreMenu({ groups, testid, compact = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { left, top } — fixed, measured after open
  const ref = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);
  // The court card clips overflow, so the menu floats above the page (portal)
  // next to its button: below it when there is room, otherwise above it.
  useLayoutEffect(() => {
    if (!open || !ref.current || !menuRef.current) { setPos(null); return; }
    const b = ref.current.getBoundingClientRect();
    const m = menuRef.current.getBoundingClientRect();
    const gap = 8;
    const left = Math.max(8, Math.min(b.right - m.width, window.innerWidth - m.width - 8));
    const below = b.bottom + gap + m.height <= window.innerHeight - 8;
    const top = below ? b.bottom + gap : Math.max(8, b.top - gap - m.height);
    setPos({ left, top });
  }, [open]);
  const shown = groups.map(g => ({ ...g, items: g.items.filter(Boolean) })).filter(g => g.items.length);
  if (!shown.length) return null;
  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid={testid} aria-haspopup="menu" aria-expanded={open}
        aria-label={compact ? 'More actions' : undefined}
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-bold ${compact ? 'w-11 h-full min-h-[40px]' : 'px-3.5 py-2'}`}
        style={{ background: open ? '#222' : '#161616', color: '#E5E5E5', border: `1px solid ${C.line}` }}>
        {compact ? <span style={{ fontSize: 18, lineHeight: 1 }}>⋯</span>
          : <>More <ChevronDown className="h-4 w-4" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms' }} /></>}
      </button>
      {open && createPortal(
        <div ref={menuRef} role="menu" data-testid={`${testid}-menu`}
          className="fixed w-72 rounded-xl p-1.5 z-[70]"
          style={{ left: pos?.left ?? -9999, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden',
                   background: '#161616', border: `1px solid ${C.line}`, boxShadow: '0 16px 40px rgba(0,0,0,0.7)' }}>
          {shown.map(g => (
            <div key={g.title} className="py-1">
              <p className="px-2.5 pt-1 pb-1 text-[10px] font-extrabold uppercase" style={{ color: '#666', letterSpacing: 1.5 }}>{g.title}</p>
              {g.items.map(it => (
                <button key={it.testid || it.label} role="menuitem" type="button" data-testid={it.testid}
                  disabled={it.disabled}
                  onClick={() => { setOpen(false); it.onClick(); }}
                  className="w-full text-left flex items-start gap-2.5 px-2.5 py-2 rounded-lg hover:bg-[#222]"
                  style={{ color: '#E5E5E5', opacity: it.disabled ? 0.45 : 1 }}>
                  {it.icon && <it.icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#A3A3A3' }} />}
                  <span>
                    <span className="block text-sm font-semibold">{it.label}</span>
                    {it.hint && <span className="block text-[11px]" style={{ color: '#737373' }}>{it.hint}</span>}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function StripChip({ value, label, color = '#fff', testid }) {
  return (
    <span data-testid={testid} className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-lg text-sm"
      style={{ background: '#0B0B0B', border: '1px solid #1E1E1E', color: '#BDBDBD' }}>
      <b style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, color }}>{value}</b>{label}
    </span>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-sm font-bold uppercase mb-2" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>
      {children}
    </h3>
  );
}

function StatusSessionBadge({ status }) {
  const colors = {
    draft: '#737373', registration_open: '#22C55E', registration_closed: '#F59E0B', active: '#E63946', ended: '#3B82F6',
  };
  const labels = {
    draft: 'Draft', registration_open: 'Registration Open', registration_closed: 'Reg Closed', active: 'LIVE', ended: 'Ended',
  };
  const c = colors[status] || '#737373';
  return (
    <span className="text-xs font-bold uppercase px-3 py-1 rounded-full"
      style={{ background: `${c}22`, color: c, border: `1px solid ${c}44` }}>
      {status === 'active' && <span className="inline-block h-2 w-2 rounded-full mr-1.5 animate-pulse" style={{ background: c }} />}
      {labels[status] || status}
    </span>
  );
}

function SmallBtn({ icon: Icon, label, color, onClick, testid, loading }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={loading} title={label}
      aria-label={label}
      className="p-2 rounded-lg transition-all"
      style={{
        color,
        background: `${color}12`,
        border: `1px solid ${color}33`,
        opacity: loading ? 0.4 : 1,
        minWidth: 36, minHeight: 36,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = `${color}26`; }}
      onMouseLeave={e => { e.currentTarget.style.background = `${color}12`; }}>
      <Icon className="h-4 w-4" />
    </button>
  );
}

// ─────────── Open Play Dashboard tab (mirrors Tournament Dashboard) ───────────
const OP_PAY_STATUS_UI = {
  paid:            { color: '#22C55E', label: 'Paid',        icon: Check },
  deposit_paid:    { color: '#3B82F6', label: '50% Paid',    icon: CreditCard },
  pending:         { color: '#F59E0B', label: 'Proof',       icon: AlertCircle },
  awaiting_payment:{ color: '#8B5CF6', label: 'Online ⏳',   icon: Clock },
  at_venue:        { color: '#737373', label: 'At Venue',    icon: Users },
  free:            { color: '#404040', label: 'Free',        icon: Check },
};

function OpenPlayDashboard({ sessionId, session, onCollect }) {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sportsApi.get(`/sports/open-play/${sessionId}/dashboard`)
      .then(r => setDash(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin w-6 h-6 rounded-full"
        style={{ border: '2px solid #E63946', borderTopColor: 'transparent' }} />
    </div>
  );
  if (!dash) return null;

  const hasFees = dash.entry_fee > 0;
  const pct = dash.revenue.total_expected > 0
    ? Math.round((dash.revenue.collected / dash.revenue.total_expected) * 100) : 0;

  // Group players by division
  const groupByDivision = (players, divisions) => {
    if (!divisions || divisions.length === 0) return [{ divId: null, divName: null, players }];
    const groups = divisions.map(d => ({
      divId: d.id, divName: d.name, players: players.filter(p => p.division_id === d.id),
    }));
    const unassigned = players.filter(p => !divisions.find(d => d.id === p.division_id));
    if (unassigned.length > 0) groups.push({ divId: 'unassigned', divName: 'Unassigned', players: unassigned });
    return groups.filter(g => g.players.length > 0);
  };

  return (
    <div data-testid="op-dashboard" className="space-y-5">
      {/* Revenue cards */}
      {hasFees && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Collected', value: `₱${dash.revenue.collected.toLocaleString()}`, color: '#22C55E', sub: `${dash.payments.paid} players paid` },
            { label: 'Pending', value: `₱${dash.revenue.pending.toLocaleString()}`, color: '#FFB300', sub: `${dash.payments.pending} players unpaid` },
            { label: 'Expected Total', value: `₱${dash.revenue.total_expected.toLocaleString()}`, color: '#fff', sub: `${dash.total_players} players × ₱${dash.entry_fee}` },
            { label: 'Collection Rate', value: `${pct}%`, color: pct >= 80 ? '#22C55E' : pct >= 50 ? '#FFB300' : '#EF4444', sub: `${dash.payments.paid}/${dash.total_players} paid` },
          ].map(({ label, value, color, sub }) => (
            <div key={label} className="rounded-xl p-4"
              style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#555' }}>{label}</p>
              <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Oswald, sans-serif', color }}>{value}</p>
              <p className="text-xs mt-1" style={{ color: '#444' }}>{sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {hasFees && (
        <div className="op-card rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide font-bold" style={{ color: '#555' }}>Payment Progress</p>
            <p className="text-xs font-bold" style={{ color: '#22C55E' }}>{dash.payments.paid}/{dash.total_players}</p>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: '#1A1A1A', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`, borderRadius: 4,
              background: pct >= 80 ? '#22C55E' : pct >= 50 ? '#FFB300' : '#EF4444',
              transition: 'width 0.4s',
            }} />
          </div>
        </div>
      )}

      {/* Match/slot progress */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Slots Completed', value: dash.matches.completed, color: '#22C55E' },
          { label: 'Live Now', value: dash.matches.live, color: '#E63946' },
          { label: 'Queued', value: dash.matches.pending, color: '#737373' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-xl p-3 text-center"
            style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900, color }}>{value}</p>
            <p className="text-xs" style={{ color: '#555' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Registered per division */}
      {(session.divisions?.length > 0) && (
        <div>
          <p className="text-xs uppercase tracking-wide font-bold mb-3" style={{ color: '#555' }}>Registered Per Division</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {session.divisions.map(d => {
              const count = dash.teams.filter(p => p.division_id === d.id).length;
              const cap = d.hard_cap || 0;
              return (
                <div key={d.id} data-testid={`op-dash-division-${d.id}`}
                  className="rounded-xl p-3" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
                  <p className="text-xs uppercase font-bold truncate" style={{ color: '#E63946' }}>{d.name}</p>
                  <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 26, fontWeight: 900, color: '#fff' }}>
                    {count}{cap ? <span style={{ fontSize: 14, color: '#555' }}> / {cap}</span> : null}
                  </p>
                  <p className="text-xs" style={{ color: '#444' }}>player{count !== 1 ? 's' : ''}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Player payment list */}
      {hasFees && (
        <div>
          <p className="text-xs uppercase tracking-wide font-bold mb-3" style={{ color: '#555' }}>Player Payment Status</p>
          <div className="space-y-4">
            {groupByDivision(dash.teams, session.divisions).map(g => (
              <div key={g.divId || 'all'}>
                {g.divName && <p className="text-xs uppercase font-bold mb-2 px-1" style={{ color: '#E63946' }}>{g.divName}</p>}
                <div className="space-y-1.5">
                  {g.players.map(p => {
                    const st = OP_PAY_STATUS_UI[p.payment_status] || OP_PAY_STATUS_UI.free;
                    const StIcon = st.icon;
                    const collectible = onCollect && !['paid', 'free'].includes(p.payment_status);
                    return (
                      <div key={p.id} data-testid={`op-dash-player-${p.id}`}
                        className="flex items-center justify-between p-3 rounded-lg"
                        onClick={collectible ? () => onCollect(p.id) : undefined}
                        title={collectible ? 'Open in Payments to collect' : undefined}
                        style={{ background: '#0F0F0F', border: '1px solid #1A1A1A',
                                 cursor: collectible ? 'pointer' : 'default' }}>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold" style={{ color: '#fff' }}>{p.team_name}</p>
                          <p className="text-xs" style={{ color: '#555' }}>
                            {p.registered_by === 'invitation' ? 'invited' : p.registered_by === 'public' ? 'self-registered' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {p.amount_due > 0 && <span className="text-xs font-bold" style={{ color: '#888' }}>₱{p.amount_due.toLocaleString()}</span>}
                          <span className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                            style={{ background: st.color + '18', color: st.color }}>
                            <StIcon className="h-3 w-3" /> {st.label}
                          </span>
                          {collectible && <span className="text-[10px] font-bold" style={{ color: '#F59E0B' }}>Collect →</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasFees && (
        <div className="rounded-xl p-8 text-center" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
          <DollarSign className="h-8 w-8 mx-auto mb-2" style={{ color: '#2A2A2A' }} />
          <p className="text-sm" style={{ color: '#737373' }}>This is a free session — no entry fees to track</p>
          <p className="text-xs mt-1" style={{ color: '#444' }}>{dash.total_players} players registered · {dash.matches.total} slots</p>
        </div>
      )}
    </div>
  );
}

// ─────────── Revenue Summary widget ───────────
function RevenueSummary({ revenue }) {
  const { totals, money, entry_fee } = revenue;
  const items = [
    { label: 'Collected', value: `₱${money.total_collected.toLocaleString()}`, sub: `${totals.paid_count} paid · ${totals.deposit_count + (totals.partial_count || 0)} part-paid`, color: '#22C55E' },
    { label: 'Balances Owed', value: `₱${money.deposits_owed.toLocaleString()}`, sub: `${totals.deposit_count + (totals.partial_count || 0)} player${(totals.deposit_count + (totals.partial_count || 0)) === 1 ? '' : 's'} · partly paid`, color: '#3B82F6' },
    { label: 'At Venue', value: `₱${money.at_venue_expected.toLocaleString()}`, sub: `${totals.at_venue_count} pay-on-arrival`, color: '#F59E0B' },
    { label: 'Awaiting', value: `₱${(money.proof_pending_expected + money.online_pending_expected).toLocaleString()}`, sub: `${totals.pending_proof_count} proof · ${totals.awaiting_online_count} online`, color: '#8B5CF6' },
  ];
  return (
    <div data-testid="op-revenue-widget" className="op-card mb-6 p-4 rounded-xl" style={{ background: '#141414', border: '1px solid #262626' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4" style={{ color: '#22C55E' }} />
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#22C55E', fontFamily: 'Oswald, sans-serif' }}>
            Revenue Summary
          </p>
        </div>
        <p className="text-xs" style={{ color: '#737373' }}>
          Entry ₱{entry_fee} · Expected total ₱{money.grand_total_expected.toLocaleString()}
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.label} data-testid={`op-rev-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="p-3 rounded-lg" style={{ background: '#0F0F0F', border: '1px solid #262626' }}>
            <p className="text-lg font-bold tabular-nums" style={{ color: it.color, fontFamily: 'Oswald, sans-serif' }}>{it.value}</p>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: '#737373' }}>{it.label}</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#555' }}>{it.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────── Invitations panel ───────────
const OP_INV_STATUS = {
  pending:   { label: 'Pending',   color: '#E63946' },
  accepted:  { label: 'Accepted',  color: '#22C55E' },
  declined:  { label: 'Declined',  color: '#EF4444' },
  expired:   { label: 'Expired',   color: '#737373' },
  cancelled: { label: 'Cancelled', color: '#737373' },
};

function OpenPlayInvitationsPanel({ sessionId, session, invitations, onRefresh }) {
  const divisions = session.divisions || [];
  const emptyRow = () => ({ name: '', email: '', division_id: divisions.length === 1 ? divisions[0].id : '' });
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const divName = (id) => divisions.find(d => d.id === id)?.name;

  const updateRow = (i, key, val) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addRow = () => setRows(rs => [...rs, emptyRow()]);
  const removeRow = (i) => setRows(rs => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);

  const norm = (s) => (s || '').trim().toLowerCase();
  const existingEmails = new Set(
    invitations.filter(i => ['pending', 'accepted'].includes(i.status)).map(i => norm(i.email))
  );
  const dupNote = (i) => {
    const r = rows[i];
    const e = norm(r.email);
    if (!e) return null;
    const firstIdx = rows.findIndex(x => norm(x.email) === e);
    if (firstIdx !== -1 && firstIdx < i) return `Same email as row ${firstIdx + 1}`;
    if (existingEmails.has(e)) return 'This email was already invited';
    return null;
  };

  const inp = { width: '100%', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 14, outline: 'none' };

  const send = async () => {
    const filled = rows.map((r, i) => ({ ...r, _i: i })).filter(r => r.name.trim() && r.email.trim());
    if (filled.length === 0) { toast.error('Add at least one name + email'); return; }
    if (divisions.length > 0) {
      const missing = filled.find(r => !r.division_id);
      if (missing) { toast.error(`Row ${missing._i + 1}: please select a division`); return; }
    }
    const dups = filled.map(r => ({ row: r._i + 1, note: dupNote(r._i), email: r.email })).filter(d => d.note);
    if (dups.length > 0) {
      const msg = 'Possible duplicates:\n\n' + dups.map(d => `• Row ${d.row} (${d.email}) — ${d.note}`).join('\n') + '\n\nSend anyway?';
      if (!window.confirm(msg)) return;
    }
    setSending(true); setResults(null);
    try {
      const r = await sportsApi.post(`/sports/open-play/${sessionId}/invitations`, {
        invitations: filled.map(r => ({
          name: r.name.trim(), email: r.email.trim(),
          division_id: r.division_id || null,
        })),
      });
      const res = r.data.results || [];
      setResults(res);
      const sent = res.filter(x => x.status === 'sent').length;
      const skipped = res.filter(x => x.status === 'skipped').length;
      if (sent) toast.success(`${sent} invitation${sent > 1 ? 's' : ''} sent${skipped ? `, ${skipped} skipped` : ''}`);
      else toast.error(skipped ? `All ${skipped} skipped — see details` : 'Nothing sent');
      const remaining = filled
        .filter((_, idx) => res[idx] && res[idx].status === 'skipped')
        .map(r => ({ name: r.name, email: r.email, division_id: r.division_id }));
      setRows(remaining.length ? remaining : [emptyRow(), emptyRow(), emptyRow()]);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const cancelInvite = async (invId) => {
    setCancelling(invId);
    try {
      await sportsApi.delete(`/sports/open-play/${sessionId}/invitations/${invId}`);
      toast.success('Invitation cancelled — slot released');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setCancelling(null); }
  };

  const pending = invitations.filter(i => i.status === 'pending');
  const accepted = invitations.filter(i => i.status === 'accepted');
  const other = invitations.filter(i => !['pending', 'accepted'].includes(i.status));
  const filledCount = rows.filter(r => r.name.trim() && r.email.trim()).length;

  return (
    <div className="space-y-5">
      {/* Bulk invite form */}
      <div data-testid="op-invite-form" className="rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4" style={{ color: '#E63946' }} />
            <p className="text-xs uppercase tracking-wide font-bold" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>
              Invite Players
            </p>
          </div>
          <span className="text-xs" style={{ color: '#525252' }}>{filledCount} ready · up to 50</span>
        </div>
        <div className="mb-3 p-2.5 rounded-lg" style={{ background: 'rgba(230,57,70,0.06)', border: '1px solid rgba(230,57,70,0.2)' }}>
          <p className="text-[11px]" style={{ color: '#A3A3A3' }}>
            Each invited player <strong style={{ color: '#fff' }}>reserves 1 seat</strong> in the division —
            beyond your invite list, additional players can still join through the public link (waitlist if the seats fill).
            Ask for an <strong style={{ color: '#fff' }}>active email</strong> — that&apos;s their key for self check-in, pause requests,
            and live queue updates on game day.
          </p>
        </div>

        <div className="space-y-2">
          {rows.map((r, i) => {
            const note = dupNote(i);
            return (
              <div key={i} data-testid={`op-invite-row-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="text-xs font-bold tabular-nums pt-2.5" style={{ color: '#525252', width: 18 }}>{i + 1}</span>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input data-testid={`op-invite-name-${i}`} value={r.name}
                      onChange={e => updateRow(i, 'name', e.target.value)} placeholder="Player name" style={inp} />
                    <input data-testid={`op-invite-email-${i}`} value={r.email}
                      onChange={e => updateRow(i, 'email', e.target.value)} placeholder="active@email.com"
                      type="email" style={{ ...inp, borderColor: note ? '#E6394666' : '#2A2A2A' }} />
                    {divisions.length > 1 && (
                      <select data-testid={`op-invite-div-${i}`} value={r.division_id}
                        onChange={e => updateRow(i, 'division_id', e.target.value)}
                        style={{ ...inp, cursor: 'pointer' }}>
                        <option value="">Select division *</option>
                        {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    )}
                  </div>
                  <button data-testid={`op-invite-remove-${i}`} onClick={() => removeRow(i)} title="Remove row"
                    className="pt-2.5" style={{ color: '#525252' }}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {note && (
                  <p data-testid={`op-invite-dup-${i}`} className="text-xs ml-6 mt-0.5 flex items-center gap-1"
                    style={{ color: '#E63946' }}>
                    <AlertCircle className="h-3 w-3" /> {note}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <button data-testid="op-invite-add-row" onClick={addRow}
          className="mt-3 flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg"
          style={{ background: '#1A1A1A', color: '#E63946', border: '1px solid #2A2A2A' }}>
          <Plus className="h-3.5 w-3.5" /> Add row
        </button>

        <button data-testid="op-invite-send" onClick={send} disabled={sending || filledCount === 0}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold uppercase"
          style={{ background: '#E63946', color: '#fff', fontFamily: 'Oswald, sans-serif',
            opacity: (sending || filledCount === 0) ? 0.5 : 1,
            cursor: (sending || filledCount === 0) ? 'not-allowed' : 'pointer' }}>
          <Mail className="h-4 w-4" /> {sending ? 'Sending…' : `Send ${filledCount || ''} Invitation${filledCount === 1 ? '' : 's'}`}
        </button>

        {results && (
          <div data-testid="op-invite-results" className="mt-3 rounded-lg p-3 space-y-1"
            style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
            <p className="text-xs font-bold uppercase mb-1" style={{ color: '#737373' }}>Send results</p>
            {results.map((x, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <span style={{ color: '#A3A3A3' }}>
                  {x.name || x.email || '(row)'} <span style={{ color: '#555' }}>· {x.email}</span>
                </span>
                <span className="font-bold" style={{ color: x.status === 'sent' ? '#22C55E' : '#E63946' }}>
                  {x.status === 'sent' ? '✓ Sent' : `⚠ Skipped${x.reason ? ` — ${x.reason}` : ''}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary counters */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pending', count: pending.length, color: '#E63946' },
          { label: 'Accepted', count: accepted.length, color: '#22C55E' },
          { label: 'Declined / Expired', count: other.length, color: '#737373' },
        ].map(({ label, count, color }) => (
          <div key={label} className="rounded-xl p-3 text-center" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900, color }}>{count}</p>
            <p className="text-xs" style={{ color: '#737373' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Invitation list */}
      <div className="space-y-2">
        {invitations.length === 0 && (
          <div className="rounded-xl p-8 text-center" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
            <Mail className="h-8 w-8 mx-auto mb-2" style={{ color: '#2A2A2A' }} />
            <p className="text-sm" style={{ color: '#737373' }}>No invitations sent yet</p>
          </div>
        )}
        {invitations.map(inv => {
          const st = OP_INV_STATUS[inv.status] || OP_INV_STATUS.pending;
          return (
            <div key={inv.id} data-testid={`op-invitation-${inv.id}`}
              className="flex items-center justify-between p-3 rounded-lg"
              style={{ background: '#0F0F0F', border: `1px solid ${inv.status === 'pending' ? '#E6394630' : '#2A2A2A'}` }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold" style={{ color: '#fff' }}>{inv.name}</p>
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full"
                    style={{ background: st.color + '22', color: st.color }}>{st.label}</span>
                  {inv.division_id && divName(inv.division_id) && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full"
                      style={{ background: '#E6394618', color: '#E63946' }}>{divName(inv.division_id)}</span>
                  )}
                </div>
                <p className="text-xs" style={{ color: '#555' }}>{inv.email}</p>
                {inv.responded_at && (
                  <p className="text-xs" style={{ color: '#444' }}>
                    Responded: {fmtDate(inv.responded_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(inv.status === 'pending' || inv.status === 'accepted') && (
                  <button data-testid={`op-cancel-inv-${inv.id}`} onClick={() => cancelInvite(inv.id)}
                    disabled={cancelling === inv.id}
                    className="text-xs px-2 py-1 rounded"
                    style={{ background: '#1A1A1A', color: '#EF4444', border: '1px solid #2A2A2A' }}>
                    {cancelling === inv.id ? '…' : 'Cancel'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
